/**
 * LevelFilterPopoverOrganism
 *
 * The provenance-aware filter surface (EAINT-781, spec § UI Components).
 * Scope-generic: takes a `scopeId` (a `ContextLevel.id`) and a
 * `ComposedLevelFilters` and knows nothing about columns, TreeMap, or
 * contexts — Atlas/Kanban/Table can adopt it unchanged, and TreeMap's
 * column-filter wiring (Phase 6) supplies the `columnIndex → levelId`
 * mapping outside this component.
 *
 * IA (dev-handoff artboard, transcribed in the spec): stacked provenance
 * groups in contract order (context → view → user → session), newest-editable
 * last, an embedded `UnifiedFilterTreeMolecule` for authoring a new draft clause,
 * an optional conflict notice, and the save-target row pinned to the bottom.
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSTransitionProps } from 'primereact/csstransition';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { OrgAvailableTypeSchema } from 'types/schema';
import type { UnifiedFilters, FilterExpression, FilterGroup, FilterGroupLeaf, ArcFilter } from 'types/filters';
import type { ComposedLevelFilters, FilterLayer } from 'types/contextView';
import { groupComposedFiltersByLayer, type LevelFilterTierRow } from '../../../utils/composeLevelFilters';
import {
  findConflictingClause,
  findConflictsByLeaf,
  laneValueClauses,
  type LaneValueClause,
} from '../../../utils/detectFilterConflict';
import {
  hasAnyFilterLane,
  concatUnifiedFilters,
  isArcLeaf,
  isLaneLeaf,
  leavesToUnifiedLanes,
  unifiedLanesToLeaves,
} from '@center-platform/spheres/filters';
import type { FilterState } from '../../../types/filterState';
import { filterStateToUnified, simpleGroupToUnified, unifiedGroupToSimple } from '../../../utils/contextFlowSerialization';
import type { PropertyOption } from '../../molecules/PropertyFilter';
import type { PerspectiveOption } from '../../molecules/PerspectiveFilter';
import type { RelationshipOption } from '../../molecules/RelationshipFilter';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import FilterProvenanceGroupMolecule from '../../molecules/FilterProvenanceGroup/FilterProvenanceGroupMolecule';
import FilterConflictNoticeMolecule, { CONFLICT_TIER_KEY } from '../../molecules/FilterConflictNotice/FilterConflictNoticeMolecule';
import FilterSaveTargetMolecule from '../../molecules/FilterSaveTarget/FilterSaveTargetMolecule';
import { SAVE_TARGET_REASON_KEY } from '../../../utils/filterSaveTargetReasons';
import AspectCatalogMolecule from '../../molecules/AspectCatalog/AspectCatalogMolecule';
import {
  buildArcCatalogEntries,
  mintFilterFromCatalogEntry,
  orderCatalogEntries,
  perspectiveOptionsToCatalogEntries,
  propertyOptionsToCatalogEntries,
  relationshipOptionsToCatalogEntries,
  resolveCatalogScopeSource,
} from '../../../utils/catalogBuilders';
import type { SchemaForFilters } from '../../../utils/filterPropertyBuilder';
import type { CatalogEntry } from '../../molecules/AspectCatalog/AspectCatalogMolecule';
import FilterGroupCardMolecule from '../../molecules/FilterGroupCard/FilterGroupCardMolecule';
import UnifiedFilterTreeMolecule from '../../molecules/UnifiedFilterTree/UnifiedFilterTreeMolecule';
import { withStableIds, nextFilterId } from '../../../utils/ids';
import { pruneIncompleteLeaves } from '../../../utils/filterOperators';
import AdvancedFilterEditorOrganism from '../AdvancedFilterEditor/AdvancedFilterEditorOrganism';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ConfirmDialogAtom from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import OverlayPanelAtom, { type OverlayPanelHandle } from '../../atoms/OverlayPanel/OverlayPanelAtom';
import { readsAsPredicate } from '../../../utils/cfGroup';
import type {
  FilterSaveTarget,
  FilterSaveTargetAvailability,
} from '../../../hooks/useLevelFilters';
import './LevelFilterPopoverOrganism.scss';

/**
 * Stable id for the draft tree's root group. The draft is never persisted
 * as a group (a save either flattens it into the layer's lane arrays or, in
 * group mode, wraps it in a freshly-minted one), so the root only needs an
 * identity React can key on across re-renders.
 */
const DRAFT_GROUP_ID = 'level-filter-draft-root';

/**
 * Below this much room between the anchor and the viewport bottom, the
 * inherited tiers are considered "tight" and fold on open (spec Phase 4).
 */
const TIGHT_ROOM_THRESHOLD = 320;

/**
 * Gutter kept between the popover body and the viewport's bottom edge, so the
 * cap itself never touches the edge it is measured against (spec Phase 4:
 * "cap the body to the room the final placement leaves").
 */
const VIEWPORT_BODY_GUTTER = 16;

/**
 * The four layers this popover edits/displays. Excludes `link` (EAINT-793) —
 * a shared filter link's clauses are never editable here; they replace
 * `view`/`user`/`session` in the composed `runtime` wholesale, and the
 * per-property editor this popover embeds has no notion of "edit a link".
 */
type EditableFilterLayer = Exclude<FilterLayer, 'link'>;

/**
 * What this popover DISPLAYS and counts, as opposed to what it can save to.
 * `link` belongs here: it is not editable, but
 * it is very much active — and this was the untouched mirror of the same fix
 * `FilterSummaryGroupMolecule` already took (its `LAYER_ORDER` was widened for
 * exactly this reason).
 *
 * Excluding `link` from display made the popover lie in three places at once
 * while a link filtered the column: the header badge showed no count, the
 * provenance stack rendered no chips, and with no context clause on the level
 * `activeFilterCount === 0` fired the empty state — "No items match the current
 * filters" — on a column that was visibly filtered. A recipient opening it
 * would reasonably conclude the board was unfiltered.
 *
 * `EditableFilterLayer` remains the right type for the save-target lookup —
 * see `isLayerLocked`, which is where it now earns that claim instead of
 * merely asserting it; it was the wrong type for counting and rendering.
 */
const DISPLAY_LAYER_ORDER: FilterLayer[] = ['context', 'view', 'user', 'session', 'link'];

/**
 * The tier this popover's own interactive draft (the embedded tree, the
 * per-lane draft editor, "Copy to my own", the Advanced editor seeded from
 * it) reads from and writes to. Every host that mounts this popover is a
 * column popover (Atlas's aspect/perspective/stage columns, TreeMap's
 * per-level column) — EAINT-936 moves that draft off the `session` tier
 * (browser-session-only, lost on reload) onto `user` (persisted via
 * `personalization.patchBase`), so a column edit survives a remount without
 * an explicit Save. `session` remains a distinct composed/display layer (a
 * shared link's suppression, `DISPLAY_LAYER_ORDER`) — this popover just no
 * longer authors into it.
 */
const AUTHORING_LAYER: FilterLayer = 'user';
const AUTHORING_TARGET: FilterSaveTarget = 'user';

/**
 * Locked = this popover cannot save to the layer, so its clauses render
 * read-only. `context` and `link` are locked by nature (neither is editable
 * here); the three editable layers are locked only when the host has disabled
 * that save target.
 *
 * One function rather than the same three-line predicate at the two call sites
 * — and the `editable` binding is not ceremony: it is what makes dropping the
 * `link` short-circuit a COMPILE error instead of a silently editable link
 * layer. `FilterSaveTarget` has no `'link'` member, so without the guard the
 * lookup below would quietly never match and report `locked: false`.
 */
function isLayerLocked(layer: FilterLayer, targets: FilterSaveTargetAvailability[]): boolean {
  if (layer === 'context' || layer === 'link') return true;
  const editable: EditableFilterLayer = layer;
  return targets.find((s) => s.target === editable)?.disabled === true;
}

/**
 * Every lane written explicitly empty — the session merge (`deepMergeJson`)
 * keeps any key a patch omits, so clearing the layer must name each lane.
 */
const EMPTY_LAYER: UnifiedFilters = {
  propertyFilters: [],
  perspectiveFilters: [],
  relationshipFilters: [],
  arcFilters: [],
  filterGroups: [],
};

/**
 * The session layer's remaining clauses after promoting the row's focused
 * clause to another layer — the promoted property or perspective clause is
 * removed, everything else in the session layer stays.
 */
function sessionLayerAfterPromotion(
  session: UnifiedFilters,
  focusProperty: string | undefined,
  focusPerspective: string | undefined,
  focusArc: string | undefined
): UnifiedFilters {
  if (focusProperty) {
    return {
      ...session,
      propertyFilters: (session.propertyFilters ?? []).filter((f) => f.property !== focusProperty),
    };
  }
  if (focusPerspective) {
    return {
      ...session,
      perspectiveFilters: (session.perspectiveFilters ?? []).filter(
        (f) => f.perspectiveSystemName !== focusPerspective
      ),
    };
  }
  if (focusArc) {
    return {
      ...session,
      arcFilters: (session.arcFilters ?? []).filter((f) => !isFocusedArcFilter(f, focusArc)),
    };
  }
  return EMPTY_LAYER;
}

function narrowToProperty(filters: UnifiedFilters, property: string): UnifiedFilters {
  return { propertyFilters: (filters.propertyFilters ?? []).filter((f) => f.property === property) };
}

function narrowToPerspective(filters: UnifiedFilters, perspectiveSystemName: string): UnifiedFilters {
  return {
    perspectiveFilters: (filters.perspectiveFilters ?? []).filter(
      (f) => f.perspectiveSystemName === perspectiveSystemName
    ),
  };
}

/**
 * Whether an `ArcFilter` is the one `focusArc` drills into. There is at most
 * one Stage column per Atlas view, so identity is just "the primary-arc
 * lane" — optionally narrowed further to one template when `focusArc` names
 * a `system_name` rather than the `'primary'` sentinel (see `focusArc`'s own
 * doc comment on `LevelFilterPopoverOrganismProps`).
 */
function isFocusedArcFilter(filter: ArcFilter, focusArc: string): boolean {
  return filter.arcSelector === 'primary' && (focusArc === 'primary' || filter.templateSystemName === focusArc);
}

function narrowToArc(filters: UnifiedFilters, focusArc: string): UnifiedFilters {
  return { arcFilters: (filters.arcFilters ?? []).filter((f) => isFocusedArcFilter(f, focusArc)) };
}

/**
 * The persisted clause content of a layer, independent of key order or
 * absent-vs-empty lanes — used to tell whether the session layer still holds
 * exactly what this popover last derived from its authoring tree (see
 * `sessionAuthoring`), or has since been changed by something else.
 */
function layerClauseKey(filters: UnifiedFilters): string {
  return JSON.stringify([
    filters.propertyFilters ?? [],
    filters.perspectiveFilters ?? [],
    filters.relationshipFilters ?? [],
    filters.arcFilters ?? [],
    filters.filterGroups ?? [],
  ]);
}

/**
 * Where a write deferred behind a shared-link exit came from — decides which
 * earlier pending write a new one supersedes (see `deferSave`).
 * - `draft`: the Save menu's draft. A later choice for the same draft replaces
 *   the earlier one (the user re-targeted it).
 * - `sessionAuthoring`: the session tree, or the Advanced editor seeded from it.
 *   Under a link the session tier on screen starts empty, so each of these
 *   carries everything authored there so far and the latest replaces the rest.
 * - `addition`: a one-off addition (a copied group, a new group from the
 *   editor). These accumulate.
 */
type PendingSaveSource = 'draft' | 'sessionAuthoring' | 'addition';

/**
 * A write chosen while a shared link suppressed this level's own layers,
 * waiting for the exit to land. `clauses` is only what was AUTHORED here: it is
 * added onto the layer the exit restores, never composed against the
 * suppressed (empty) layer that was on screen — which would overwrite the
 * recipient's own clauses.
 */
interface PendingSave {
  target: FilterSaveTarget;
  clauses: UnifiedFilters;
  source: PendingSaveSource;
  /** Show the "saved to …" confirmation once the write lands. */
  confirms?: boolean;
  /** The Advanced editor stays open on what was applied until the write lands. */
  closesAdvancedEditor?: boolean;
}

export interface LevelFilterPopoverOrganismProps {
  /** The `ContextLevel.id` this popover edits. Used for analytics only — never rendered. */
  scopeId: string;
  /** The composed filter stack for this level, from `useLevelFilters().byLevel[scopeId]`. */
  composed: ComposedLevelFilters;
  /**
   * Scopes every group + the embedded editor to one property (D-1: both the
   * per-property and whole-level entry points exist in the codebase).
   * Omitted → the whole level's constraints render, across every property.
   */
  focusProperty?: string;
  /**
   * Scopes every group + the embedded editor to one perspective lane (W5: an
   * Atlas `PerspectiveSelector` column). Mutually exclusive with
   * `focusProperty` — a popover drills into exactly one property OR one
   * perspective, never both.
   */
  focusPerspective?: string;
  /**
   * Scopes every group + the embedded editor to one arc lane (Atlas's Stage
   * column, an `ArcStageSelector`). Mutually exclusive with `focusProperty`
   * and `focusPerspective`. Unlike a perspective, an arc lane has no per-
   * instance catalog identity to name (`buildArcCatalogEntries` mints a
   * single "Arc" entry) and there is at most one Stage column per Atlas
   * view, so this identifies "the primary arc lane" rather than a specific
   * filter: pass the sentinel `'primary'` for the sphere's one primary arc,
   * or an arc template's `system_name` to narrow further when a view shows
   * more than one template's stages. Locks the fused arc-selector chrome
   * (`ArcValueEditorMolecule`'s `lockArcSelector`) so the host's column
   * choice can't be re-picked from inside the popover.
   */
  focusArc?: string;
  /**
   * The view-wide (`'*'`) scope's context tier (`useLevelFilters({
   * viewWideAspect }).globalContextTiers`) — one row per level with a clause
   * on the aspect this popover is scoped to, labelled by level. A per-level
   * scope's own `composed.context` already carries that level's clause, so
   * this is `undefined` there. The view-wide scope has no single `context`
   * value at all — `useLevelFilters` composes it with `context: undefined` —
   * so without this, Atlas's column popover renders no context tier and can
   * never flag a conflict against it (EAINT-931 W6).
   */
  contextTierRows?: LevelFilterTierRow[];
  /** Rendered as a scope strip beneath the header — e.g. "Applies to all 3 segments in this level." */
  scopeNote?: string;
  /** Whether the caller holds `edit` on the CONTEXT (D-G) — gates "Edit in context". */
  canEditContext: boolean;
  onEditInContext?: () => void;
  /** Always all three targets, in blast-radius order — from `useLevelFilters`. */
  saveTargets: FilterSaveTargetAvailability[];
  /**
   * Void for a fire-and-forget host (the debounced personalization write
   * never rejects by design — see `useViewPersonalization`). A host that DOES
   * await a real network write returns its promise; a rejection is what
   * drives the save-failure footer (retryable vs. not, Retry re-sends).
   */
  onSave: (filters: UnifiedFilters, target: FilterSaveTarget) => void | Promise<void>;
  /**
   * Whether an active shared filter link is suppressing this level's own
   * `view`/`user`/`session` layers (EAINT-793 — `useLevelFilters`'s
   * `linkActive`). Stated explicitly rather than inferred, because it decides
   * whether a save may be composed against `composed` at all.
   */
  linkSuppressed?: boolean;
  /**
   * Leaves shared-link mode (`useSharedFilterLink().exit`). Required whenever
   * `linkSuppressed` can be true, because a save under suppression is
   * performed only AFTER this resolves (see `handleSave`). A host that cannot
   * exit gets its save targets disabled rather than a save that never lands.
   */
  onExitSharedLink?: () => void;
  contextSystemName?: string | null;

  availableProperties: PropertyOption[];
  /**
   * Unscoped org-wide catalog, forwarded to the advanced editor's rail so it can
   * tier `availableProperties` (this level's scope) above everything else while
   * keeping out-of-scope aspects findable by search.
   */
  orgWideProperties?: PropertyOption[];
  /**
   * Load state for the aspect catalog. Passed in rather than inferred from an
   * empty `availableProperties`, because "still loading", "failed to load" and
   * "genuinely none" are three different things that previously all rendered as
   * "No aspects match." (EAINT-781).
   */
  propertiesLoading?: boolean;
  propertiesError?: string | null;
  availablePerspectives: PerspectiveOption[];
  availableRelationships?: RelationshipOption[];
  arcTemplates?: ArcTemplateOption[];
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  /**
   * The level's own type scope, as system_names. Forwarded to the advanced
   * editor so its rail can tier in-scope types above the rest; without it the
   * rail has no scope and renders a single flat list (EAINT-781).
   */
  scopeTypes?: string[];
  /**
   * Every org schema, keyed for `resolveCatalogScopeSource` — the popover
   * derives its own catalogs' `scopeTypeLabels` from this plus the context/
   * view/user tiers' own `type` leaves (wherever nested), rather than relying
   * solely on a caller-computed `scopeTypes`. Omitted callers keep the
   * pre-EAINT-936 behaviour: no computed scope, catalogs fall back to
   * `scopeNote`/the generic "no sphere type" nudge.
   */
  allSchemas?: SchemaForFilters[];
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  getPropertyLabel?: (propertyKey: string) => string;
  getPerspectiveLabel?: (perspectiveSystemName: string) => string;
  getRelationshipLabel?: (relationshipSystemName: string) => string;
  getPropertyValueLabel?: (propertyKey: string, value: string) => string;

  /** Trigger button tooltip / label — e.g. the column's name. */
  triggerLabel: string;
  /**
   * Whether this organism renders its own filter trigger and the `OverlayPanelAtom`
   * layer that trigger opens. `false` renders the PANEL BODY ALONE, inline, for a host
   * that already owns both — a column header with its own filter glyph, a toolbar, an
   * artboard mounting the panel to document it. The two travel together on purpose:
   * nothing else can open the overlay (no imperative show/hide is exposed), so a panel
   * left inside it with the trigger gone would be unreachable. In that mode the header's
   * close button calls `onClose` — the host dismisses its own layer — and `onOpen` is
   * the host's to fire, since the panel is open from the moment it mounts.
   *
   * A boolean rather than a `renderTrigger` callback because the trigger is not
   * parameterisable: its icon, its count badge and its live-region status are all
   * derived from state this organism owns (`activeFilterCount`, `hasSessionFilter`),
   * and handing those to a host would publish internals no caller asked for. Every
   * host that wants a different trigger already has one.
   *
   * Defaults to `true` — today's trigger-plus-overlay render, unchanged.
   */
  trigger?: boolean;
  /**
   * Fires when the popover opens — Atlas's column adapter uses this to tell
   * the shared `useLevelFilters` hook which column's aspect is now active, so
   * its `viewWideAspect`/`globalContextTiers` (and this popover's
   * `contextTierRows`) narrow to the column actually open (EAINT-931 W6).
   * A per-level scope (TreeMap) has no such shared hook to steer and omits it.
   */
  onOpen?: () => void;
  /**
   * Fires when the popover closes (dismiss, click-outside, Escape) —
   * Atlas's column adapter uses this to clear the column it told
   * `onOpen` was active, so `viewWideAspect`/`globalContextTiers` return to
   * empty rather than staying narrowed to the last-opened column
   * (EAINT-931 W6 review fix).
   */
  onClose?: () => void;
  /** Whether this column is currently hidden (dims it at 45% with struck-through label). */
  isColumnHidden?: boolean;
  /** Called when the user toggles "Hide this column". */
  onHideColumn?: () => void;
  /** Called when the user clicks "Reset mine" — drops session-layer filters only. */
  onResetSession?: () => void;
  /**
   * Fires when the user confirms "Share with view…" on a column/level whose
   * filters are saved to the `user` tier — receives this column's own
   * user-tier clauses so the host can write them to the view. Only offered
   * while there is something to share (`hasSessionToPromote`); once the call
   * resolves the popover clears its own local copies (OQ-1: move, not copy).
   */
  onShareWithView?: (clauses: UnifiedFilters) => void | Promise<void>;
  /**
   * Fires when `onShareWithView` rejects with a 403 — mirrors
   * `useLevelFilters`'s option of the same name (`commitPendingSharedFilters`'s
   * 403 path). The host bumps its permission-cache epoch
   * (`resetContextViewPermissionsCache`) so the next check re-probes rather
   * than staying fail-closed for the rest of the session.
   */
  onViewPermissionDenied?: () => void;
  /**
   * Display name of the active view — shown in the "no_view_edit_permission"
   * save-target reason so users know which view to ask for edit on.
   */
  activeViewDisplayName?: string;
  className?: string;
}

/** Module-level so the default is one stable function, not a new one per render. */
const identityPropertyLabel = (key: string): string => key;

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The per-lane editor's header: the picked aspect's or perspective's label
 * (falling back to its key), or the picker's prompt while nothing is picked.
 */
function resolvePropertyEditorTitle(
  pickedProperty: string | null,
  availableProperties: PropertyOption[],
  availablePerspectives: PerspectiveOption[],
  t: Translate,
  focusArc?: string
): string {
  if (!pickedProperty) return t('columnFilters.chooseProperty');
  const found =
    availableProperties.find((p) => p.key === pickedProperty)?.label ??
    availablePerspectives.find((p) => p.systemName === pickedProperty)?.label;
  if (found) return found;
  // An arc lane has no catalog entry per instance (`buildArcCatalogEntries`
  // mints one "Arc" row) — `pickedProperty` holds `focusArc`'s own value
  // (a sentinel or a template system_name), which is never a label.
  if (focusArc) return t('filters.properties.arc');
  return pickedProperty;
}

/** The translated reason a locked save target gives, or `undefined` when it is writable or gives none. */
function lockedTargetReason(target: FilterSaveTargetAvailability | undefined, t: Translate): string | undefined {
  if (target?.disabled !== true || !target.reason) return undefined;
  return t(SAVE_TARGET_REASON_KEY[target.reason]);
}

/** Whether there are targets and none of them can take a save. */
function allTargetsDisabled(targets: FilterSaveTargetAvailability[]): boolean {
  return targets.length > 0 && targets.every((s) => s.disabled);
}

/**
 * The in-progress authoring tree, but only while it was built on the session
 * layer that is persisted now; `null` once that layer has moved on.
 */
function currentAuthoringTree(
  authoring: { tree: FilterGroup; basisKey: string } | null,
  persistedKey: string
): FilterGroup | null {
  return authoring?.basisKey === persistedKey ? authoring.tree : null;
}

interface LevelFilterTriggerProps {
  activeFilterCount: number;
  hasSessionFilter: boolean;
  triggerLabel: string;
  onToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Trigger conventions match `ColumnFilterHeaderAtom`'s own trigger
 * button under this organism's own BEM namespace (spec § Design System
 * Reuse Audit: "Column trigger for TreeMap and Atlas"). Literal reuse
 * of that atom isn't wireable here — it owns its own OverlayPanel and a
 * single-property `FilterEditorPanelMolecule`, whereas this organism's
 * trigger opens a multi-tier provenance panel around an embedded
 * `UnifiedFilterTreeMolecule` — so the icon-state pair
 * (pi-filter-fill/pi-filter-slash), the `--active` class, and the
 * sr-only live region are reproduced here rather than the whole atom.
 */
const LevelFilterTrigger: React.FC<LevelFilterTriggerProps> = ({
  activeFilterCount,
  hasSessionFilter,
  triggerLabel,
  onToggle,
}) => {
  const { t } = useTranslation();
  const active = activeFilterCount > 0;
  return (
    <>
      <ButtonAtom
        icon={`pi ${active ? 'pi-filter-fill' : 'pi-filter-slash'}`}
        variant="text"
        size="small"
        className={`level-filter-popover__trigger${active ? ' level-filter-popover__trigger--active' : ''}`}
        onClick={onToggle}
        tooltip={t('columnFilters.triggerTooltip', { column: triggerLabel })}
        aria-label={
          active
            ? t('columnFilters.triggerAriaLabelActive', { column: triggerLabel })
            : t('columnFilters.triggerAriaLabel', { column: triggerLabel })
        }
      >
        {active && (
          <span className={`level-filter-popover__badge${hasSessionFilter ? ' level-filter-popover__badge--session' : ''}`}>
            {activeFilterCount}
          </span>
        )}
      </ButtonAtom>
      <span className="level-filter-popover__trigger-sr-only" aria-live="polite" role="status">
        {active ? t('columnFilters.triggerStatusActive', { column: triggerLabel }) : ''}
      </span>
    </>
  );
};

interface HideColumnButtonProps {
  isColumnHidden: boolean;
  onHideColumn: () => void;
}

/** Toggles the column's visibility (note #8). */
const HideColumnButton: React.FC<HideColumnButtonProps> = ({ isColumnHidden, onHideColumn }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={`level-filter-popover__hide-column${isColumnHidden ? ' level-filter-popover__hide-column--hidden' : ''}`}
      onClick={onHideColumn}
    >
      <i className={`pi ${isColumnHidden ? 'pi-eye' : 'pi-eye-slash'}`} aria-hidden="true" />
      {isColumnHidden ? t('columnFilters.showColumn') : t('columnFilters.hideColumn')}
    </button>
  );
};

const LevelFilterPopoverOrganism: React.FC<LevelFilterPopoverOrganismProps> = ({
  scopeId,
  composed,
  focusProperty,
  focusPerspective,
  focusArc,
  contextTierRows,
  scopeNote,
  canEditContext,
  onEditInContext,
  saveTargets,
  onSave,
  linkSuppressed = false,
  onExitSharedLink,
  contextSystemName,
  availableProperties,
  orgWideProperties,
  propertiesLoading = false,
  propertiesError = null,
  availablePerspectives,
  availableRelationships = [],
  arcTemplates = [],
  familyTypeSchemas = [],
  scopeTypes = [],
  allSchemas = [],
  perspectiveSearchProps,
  getPropertyLabel = identityPropertyLabel,
  getPerspectiveLabel = (name) => name,
  getRelationshipLabel = (name) => name,
  getPropertyValueLabel = (_key, value) => value,
  triggerLabel,
  trigger = true,
  onOpen,
  onClose,
  isColumnHidden = false,
  onHideColumn,
  onResetSession,
  onShareWithView,
  onViewPermissionDenied,
  activeViewDisplayName,
  className = '',
}) => {
  const { t, i18n } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const overlayRef = useRef<OverlayPanelHandle>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Stable ID for this instance — used to distinguish "I opened" from "another opened".
  const instanceId = useId();

  // Close this popover when any OTHER LevelFilterPopoverOrganism opens.
  // This enforces the single-open-at-a-time rule without prop threading or
  // ref arrays — any instance that opens broadcasts its ID; every other
  // visible instance hides itself.
  useEffect(() => {
    const handler = (e: Event) => {
      const openedId = (e as CustomEvent<string>).detail;
      if (openedId !== instanceId && overlayRef.current?.isVisible()) {
        overlayRef.current.hide();
      }
    };
    window.addEventListener('atlas-filter-open', handler);
    return () => window.removeEventListener('atlas-filter-open', handler);
  }, [instanceId]);
  const [draft, setDraft] = useState<UnifiedFilters>({});
  // The per-property drill-in's draft editor. Only a `focusProperty` popover
  // opens it (on mount, pre-bound to that property); every other add goes
  // through the session tree's own "+ Condition" / "+ Add group".
  const [showEditor, setShowEditor] = useState(!!focusProperty || !!focusPerspective || !!focusArc);
  // Step 2: property was picked — show value editor scoped to this property.
  // Pre-set to focusProperty/focusPerspective/focusArc when in per-lane mode (skips the picker step).
  const [pickedProperty, setPickedProperty] = useState<string | null>(
    focusProperty ?? focusPerspective ?? focusArc ?? null
  );
  /**
   * The leaf the catalog minted for `pickedProperty` — a `kind`-discriminated
   * lane condition for a perspective/relationship/arc/measure pick, a plain
   * property row otherwise. Held rather than re-derived because only the
   * catalog entry knows which lane the pick came from; `null` in the
   * `focusProperty` drill-in, which opens on a column property with no pick.
   * The `focusPerspective`/`focusArc` drill-ins (W5) mint their own lane leaf
   * up front the same way a catalog pick would, so the editor opens straight
   * on the value editor rather than the picker.
   */
  const mintFocusLeaf = useCallback((): FilterGroupLeaf | null => {
    if (focusPerspective) {
      const label =
        availablePerspectives.find((p) => p.systemName === focusPerspective)?.label ?? focusPerspective;
      const entry = perspectiveOptionsToCatalogEntries([{ systemName: focusPerspective, label }], t)[0];
      return mintFilterFromCatalogEntry(entry) as FilterGroupLeaf;
    }
    if (focusArc) {
      const entry = buildArcCatalogEntries(t, true)[0];
      const minted = mintFilterFromCatalogEntry(entry) as FilterGroupLeaf;
      if (!isArcLeaf(minted)) return minted;
      // The one arc catalog entry mints `arcSelector: 'any'` (the never-widen
      // seed, SC6) — a focus-arc lane is by definition the primary arc, so it
      // is pinned here rather than left for a later save-time pin to catch.
      // When exactly one template is in view there is nothing to pick, so it
      // is preselected too (uniqueTemplatePreselected) rather than leaving the
      // stage picker empty until the author re-chooses the only option.
      const onlyTemplate = arcTemplates.length === 1 ? arcTemplates[0].systemName : undefined;
      return {
        kind: 'arc',
        filter: { ...minted.filter, arcSelector: 'primary', ...(onlyTemplate ? { templateSystemName: onlyTemplate } : {}) },
      };
    }
    return null;
  }, [focusPerspective, focusArc, availablePerspectives, arcTemplates, t]);

  const [pickedLeaf, setPickedLeaf] = useState<FilterGroupLeaf | null>(mintFocusLeaf);

  // Either the chosen aspect's/perspective's label, or the picker's prompt.
  const propertyEditorTitle = resolvePropertyEditorTitle(pickedProperty, availableProperties, availablePerspectives, t, focusArc);

  const closeEditor = useCallback(() => {
    setShowEditor(false);
    setPickedProperty(null);
    setPickedLeaf(null);
  }, []);

  // Advanced filter editor state — opened from the session tier's Advanced mode
  // or a group card's View/Edit.
  interface AdvancedEditorState {
    initialFilter: FilterState;
    readOnly: boolean;
    provenance?: Extract<FilterLayer, 'context' | 'view' | 'user'>;
    canCopy: boolean;
    groupIndex?: number; // index of existing group being edited (undefined = new group)
  }
  const [advancedEditorState, setAdvancedEditorState] = useState<AdvancedEditorState | null>(null);

  const openAdvancedEditorForGroup = useCallback(
    (group: import('types/filters').FilterGroup, provenance: 'context' | 'view' | 'user', canEdit: boolean, groupIndex?: number) => {
      setAdvancedEditorState({
        // The tier's group is canonical (`expressions`); the editor edits its own
        // `SimpleFilterGroup` shape (`filters`). Convert at this boundary, and back
        // again on every write below — a cast here handed the editor a group with no
        // `filters`, and one on the way out persisted a group with no `expressions`.
        initialFilter: { propertyFilters: [], perspectiveFilters: [], filterGroups: [unifiedGroupToSimple(group)] },
        readOnly: !canEdit,
        provenance,
        canCopy: !canEdit,
        // Only tracked when editable — a locked View/Copy-to-own open never
        // writes back to this index, so there's nothing to replace.
        groupIndex: canEdit ? groupIndex : undefined,
      });
      overlayRef.current?.hide();
    },
    []
  );

  // Promotion confirmation: tracks the last-saved target for ~2.6s so the
  // tier badge flips "SESSION → IN VIEW" and a green confirmation shows.
  const [promotedTarget, setPromotedTarget] = useState<FilterSaveTarget | null>(null);
  const promotionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * A failed `onSave`/`commitSave` (see `attemptSave`) — `retryable` splits
   * 5xx/network (Retry re-sends) from 403/400 (no retry; a different action
   * is named instead). The row that produced the attempt stays open: neither
   * the draft nor the editor is cleared until a retry succeeds.
   */
  const [saveError, setSaveError] = useState<{ target: FilterSaveTarget; retryable: boolean } | null>(null);

  /**
   * A failed "Share with view…" commit — same retryable/permission split as
   * `saveError`. On a non-retryable (403) rejection, `onViewPermissionDenied`
   * fires so the host's permission cache re-probes; either way the user-tier
   * clauses are untouched, since `onSave` (the move) only runs on success.
   */
  const [shareError, setShareError] = useState<{ retryable: boolean } | null>(null);

  /**
   * The user-tier layer as it stood just before the last row removal from the
   * session tree — set only when `handleSessionTreeChange` sees the clause
   * count drop, so an edit (same count) or an addition (higher count) never
   * shows this. `handleUndoRemove` re-saves it verbatim; it is never
   * time-boxed (ux-craft: keyboard-reachable for its whole life), so it stays
   * until Undo is clicked or another removal replaces it.
   */
  const [removedUndo, setRemovedUndo] = useState<{ layer: UnifiedFilters } | null>(null);
  const lastSaveAttemptRef = useRef<{ target: FilterSaveTarget; clauses: UnifiedFilters; onSuccess: () => void } | null>(
    null
  );

  /**
   * Whether a shared filter link is currently suppressing this level's own
   * `view`/`user`/`session` layers (EAINT-793). EITHER signal is enough: the
   * explicit `linkSuppressed` prop, or `saveTargets` carrying the reason
   * `useLevelFilters` derives from the same `linkActive`. Both are read on
   * purpose — this flag is a data-loss guard, so it must not be disarmable by
   * dropping one prop (`TreeMapGrid` defaults `filterSaveTargets` to `[]`) or
   * by later changing which reason the personal targets carry.
   *
   * Deliberately NOT sniffed from `composed.provenance` — a link that carries
   * nothing for this particular level still suppresses it (presence, not
   * content, activates link mode) and contributes no `link` entries, which is
   * precisely the case where the recipient's own clauses are at risk.
   */
  const linkActive = useMemo(
    () => linkSuppressed || saveTargets.some((s) => s.reason === 'link_exits_first'),
    [linkSuppressed, saveTargets]
  );

  /**
   * A save under suppression is only safe because it is deferred behind an
   * exit. Where no exit is wired the deferral could never resolve and the
   * edit would be silently swallowed, so the personal targets are disabled
   * instead — the one case where refusing beats accepting.
   */
  const canDeferSave = linkActive && Boolean(onExitSharedLink);

  const effectiveTargets = useMemo<FilterSaveTargetAvailability[]>(() => {
    if (!linkActive || canDeferSave) return saveTargets;
    return saveTargets.map((availability) =>
      availability.disabled
        ? availability
        : { target: availability.target, disabled: true, reason: 'link_active' as const }
    );
  }, [linkActive, canDeferSave, saveTargets]);

  /**
   * Whether this popover may write the session layer at all. Every authoring
   * affordance of the session tier (the tree, the Advanced route, the per-lane
   * draft editor, "Copy to my own") writes there, so when it cannot — a link is
   * active and no exit is wired to defer behind — they render read-only with
   * the target's reason instead of accepting an edit that could never land.
   */
  const sessionTarget = effectiveTargets.find((s) => s.target === AUTHORING_TARGET);
  const sessionLocked = sessionTarget?.disabled === true;
  const sessionLockedReason = lockedTargetReason(sessionTarget, t);

  /**
   * The per-lane drill-in's draft editor authors a clause for the Save menu, so
   * it is only offered while at least one target can take it.
   */
  const everyTargetDisabled = allTargetsDisabled(effectiveTargets);
  const draftEditorOpen = showEditor && !everyTargetDisabled;

  /**
   * Every layer's FULL clause set for this level. The save path must use this,
   * never the `focusProperty`-narrowed view below — writing back a narrowed
   * layer would drop that layer's clauses on every OTHER property.
   */
  const byLayerFull = useMemo(() => {
    const grouped = groupComposedFiltersByLayer(composed);
    // The view-wide scope has no single `context` value — `useLevelFilters`
    // composes it with `context: undefined` — so its `composed.context` is
    // always empty. `contextTierRows` is the real content: merge every
    // level's row into one set so counts, conflict detection (`lockedClauses`
    // below) and the flat `byLayer.context` a per-level scope already relies
    // on all see it. Untouched when `contextTierRows` is undefined — a
    // per-level scope's own `composed.context` already carries its clause.
    if (!contextTierRows) return grouped;
    const mergedContext = contextTierRows.reduce<UnifiedFilters>(
      (acc, row) => concatUnifiedFilters(acc, row.filters) ?? acc,
      {}
    );
    return { ...grouped, context: mergedContext };
  }, [composed, contextTierRows]);

  /**
   * In-scope sphere types, derived (not caller-supplied) from every tier this
   * popover can ever author or inherit a `type` leaf on: context, view and
   * user (`AUTHORING_LAYER` — the column's own leaf lives here since Phase 1
   * moved column authoring off `session`). `resolveCatalogScopeSource` walks
   * nested groups on its own, so a `type` leaf inside an `or` group still
   * resolves (`scopeFromNestedGroups`). Falls back to `[]` (the catalogs'
   * existing "no sphere type" nudge) when no caller supplies `allSchemas`.
   */
  const scopeTypeLabels = useMemo(() => {
    if (allSchemas.length === 0) return [];
    const merged =
      concatUnifiedFilters(concatUnifiedFilters(byLayerFull.context, byLayerFull.view), byLayerFull.user) ?? {};
    const { typeSystemNames } = resolveCatalogScopeSource(merged, allSchemas);
    return typeSystemNames.map((systemName) => allSchemas.find((s) => s.system_name === systemName)?.name ?? systemName);
  }, [allSchemas, byLayerFull.context, byLayerFull.view, byLayerFull.user]);

  const byLayer = useMemo((): Record<FilterLayer, UnifiedFilters> => {
    if (focusProperty) {
      return {
        context: narrowToProperty(byLayerFull.context, focusProperty),
        view: narrowToProperty(byLayerFull.view, focusProperty),
        user: narrowToProperty(byLayerFull.user, focusProperty),
        session: narrowToProperty(byLayerFull.session, focusProperty),
        link: narrowToProperty(byLayerFull.link, focusProperty),
      };
    }
    if (focusPerspective) {
      return {
        context: narrowToPerspective(byLayerFull.context, focusPerspective),
        view: narrowToPerspective(byLayerFull.view, focusPerspective),
        user: narrowToPerspective(byLayerFull.user, focusPerspective),
        session: narrowToPerspective(byLayerFull.session, focusPerspective),
        link: narrowToPerspective(byLayerFull.link, focusPerspective),
      };
    }
    if (focusArc) {
      return {
        context: narrowToArc(byLayerFull.context, focusArc),
        view: narrowToArc(byLayerFull.view, focusArc),
        user: narrowToArc(byLayerFull.user, focusArc),
        session: narrowToArc(byLayerFull.session, focusArc),
        link: narrowToArc(byLayerFull.link, focusArc),
      };
    }
    return byLayerFull;
  }, [byLayerFull, focusProperty, focusPerspective, focusArc]);

  /**
   * The view layer's property filters, each carrying an id — normalised once on
   * the FULL list (ids minted by `withStableIds` are not deterministic, so the
   * rows and the write-back must share this one normalisation), then narrowed
   * for display. The editable view rows report the id of the row acted on.
   */
  const viewPropertyFiltersWithIds = useMemo(
    () => withStableIds(byLayerFull.view.propertyFilters ?? []),
    [byLayerFull.view.propertyFilters]
  );
  const viewRowFilters = useMemo<UnifiedFilters>(() => {
    let propertyFilters = viewPropertyFiltersWithIds;
    if (focusPerspective || focusArc) propertyFilters = [];
    else if (focusProperty) propertyFilters = viewPropertyFiltersWithIds.filter((f) => f.property === focusProperty);
    return { ...byLayer.view, propertyFilters, filterGroups: [] };
  }, [byLayer.view, focusProperty, focusPerspective, focusArc, viewPropertyFiltersWithIds]);

  const activeFilterCount = useMemo(
    () => DISPLAY_LAYER_ORDER.reduce((count, layer) => count + (hasAnyFilterLane(byLayer[layer]) ? 1 : 0), 0),
    [byLayer]
  );

  // Per-tier clause counts (number of filter clauses, not just presence)
  const countClauses = (filters: UnifiedFilters) =>
    (filters.propertyFilters?.length ?? 0) +
    (filters.perspectiveFilters?.length ?? 0) +
    (filters.relationshipFilters?.length ?? 0) +
    (filters.arcFilters?.length ?? 0) +
    (filters.filterGroups?.length ?? 0);

  const contextCount = useMemo(() => countClauses(byLayer.context), [byLayer.context]);
  const viewCount = useMemo(() => countClauses(byLayer.view), [byLayer.view]);
  const sessionCount = useMemo(() => countClauses(byLayer[AUTHORING_LAYER]), [byLayer]);

  /**
   * `inheritedFilters` (spec vocabulary — `contextFilters.length + viewFilters.length
   * > 0`): whether there is anything for "Show what the context and the view
   * already narrow" to summarize. Folding without this being true would offer
   * a control with nothing behind it.
   */
  const hasInheritedFilters = useMemo(
    () => hasAnyFilterLane(byLayer.context) || hasAnyFilterLane(byLayer.view),
    [byLayer.context, byLayer.view]
  );
  const inheritedFilterCount = contextCount + viewCount;

  // Badge goes dark blue (session variant) when a session filter is present (dev note #1)
  const hasSessionFilter = useMemo(() => hasAnyFilterLane(byLayer[AUTHORING_LAYER]), [byLayer]);

  // Tier fold starts folded whenever a session filter already exists (prototype's
  // `dirty = sessionFilters.length > 0`, one of the three `working` conditions
  // gating `inhVisible`) — you're mid-authoring, so the inherited tiers get out
  // of the way behind the "{N} inherited" summary until reopened. With no
  // session filter yet, the tiers start expanded so the full picture is visible
  // before you add anything.
  const [inheritedFolded, setInheritedFolded] = useState(hasSessionFilter);

  /**
   * Whether the anchor had tight room the last time the popover opened.
   * Measured once, on `onShow` (a discrete trigger) — never on a resize
   * observer — so it is inherently latched: growing the viewport while the
   * popover stays open does not re-measure, and the tiers stay folded until
   * the popover closes and reopens (spec Phase 4: "tightIsLatchedWhileOpen").
   */
  const [roomTight, setRoomTight] = useState(false);

  /**
   * The body's max-height, capped to the room the anchor's final placement
   * leaves to the viewport bottom minus a gutter (spec Phase 4: "cap the body
   * to the room the final placement leaves; re-measure on discrete triggers").
   * Measured alongside `roomTight`, on the same discrete trigger — never on a
   * resize observer — so the popover never renders past the viewport
   * regardless of how much content the tiers/editor hold.
   */
  const [bodyMaxHeight, setBodyMaxHeight] = useState<number | undefined>(undefined);
  const measureRoom = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const room = globalThis.window.innerHeight - rect.top;
    setRoomTight(room < TIGHT_ROOM_THRESHOLD);
    setBodyMaxHeight(Math.max(room - VIEWPORT_BODY_GUTTER, 0));
  }, []);

  /**
   * Keeps the panel a gutter clear of both viewport edges. PrimeReact aligns
   * an overflowing panel's right edge to the trigger's, so a trigger near the
   * right edge left the panel flush against it at 320px (ux-craft L1). Runs on
   * the transition's `onEnter`, straight after PrimeReact's own `align()` and
   * before the first paint — a discrete trigger, never an observer. The gutter
   * is the same `--spacing-md` the stylesheet's width cap subtracts per side,
   * so a panel at that cap lands exactly centred.
   *
   * The panel is found from the content ref: PrimeReact drives the transition
   * with a `nodeRef`, so react-transition-group calls `onEnter` without a node.
   */
  const keepPanelInsideViewport = useCallback(() => {
    const node = contentRef.current?.closest<HTMLElement>('.p-overlaypanel');
    if (!node) return;
    const gutter = Number.parseFloat(globalThis.getComputedStyle(node).getPropertyValue('--spacing-md')) || 0;
    const rect = node.getBoundingClientRect();
    const viewportWidth = globalThis.document.documentElement.clientWidth;
    let shift = 0;
    if (rect.right > viewportWidth - gutter) shift = viewportWidth - gutter - rect.right;
    if (rect.left + shift < gutter) shift = gutter - rect.left;
    if (shift === 0) return;
    node.style.left = `${(Number.parseFloat(node.style.left) || 0) + shift}px`;
    // The arrow is offset from the panel's left edge to point at the trigger;
    // moving the panel moves the arrow with it, so counter-shift it.
    const arrowLeft = Number.parseFloat(node.style.getPropertyValue('--overlayArrowLeft')) || 0;
    node.style.setProperty('--overlayArrowLeft', `${Math.max(arrowLeft - shift, 0)}px`);
  }, []);

  // PrimeReact types `transitionOptions` as a complete `CSSTransitionProps`
  // (whose union demands a `timeout` or an `addEndListener`), yet its
  // CSSTransition spreads the options as a partial override over the overlay's
  // own timeout and class names (`{...mutableProps, ...options, ...immutable}`)
  // and chains `onEnter` after its own `align()`. Supplying a timeout here would
  // override the overlay's, so the one hook this popover adds is all it passes;
  // the library's declaration is what is too strict, hence the assertion.
  const panelTransitionOptions = useMemo(
    () => ({ onEnter: keepPanelInsideViewport }) as Partial<CSSTransitionProps> as CSSTransitionProps,
    [keepPanelInsideViewport]
  );

  /**
   * Tier fold starts the moment authoring starts — a session row already
   * exists (an incomplete one included, see `sessionTreeHasRows` below; spec:
   * "a row exists or is open — not 'has a valid filter'") — or the anchor's
   * room is tight. Forward-only: nothing here unfolds the tiers, that is only
   * ever a direct user action (the summary's own click).
   *
   * The per-property drill-in's draft editor is deliberately not a trigger:
   * it is open from mount because the whole popover IS that one property's
   * editor, not because the user started authoring — folding on it would hide
   * the very context/view chips the drill-in exists to show.
   */
  useEffect(() => {
    if (hasSessionFilter || roomTight) setInheritedFolded(true);
  }, [hasSessionFilter, roomTight]);

  /**
   * The folded line's clause summary — every context and view clause, each in
   * the reader's words through the same `readsAsPredicate` the tree's "Reads
   * as" sentence uses (one clause per lane leaf, one per group), joined by " · "
   * (context/column-filter.jsx:482). A summary that hid the constraints would
   * defeat the fold: the line must still state what narrows the column.
   */
  const inheritedSummary = useMemo(() => {
    const readsAsContext = {
      t,
      availableProperties,
      availableRelationships,
      availablePerspectives,
      locale: i18n?.language,
      getPropertyLabel,
    };
    const clauseTexts = (['context', 'view'] as const).flatMap((layer) => {
      const filters = byLayer[layer];
      return [
        ...unifiedLanesToLeaves(filters).map((leaf) =>
          readsAsPredicate({ operator: 'and', expressions: [leaf] }, readsAsContext)
        ),
        ...(filters.filterGroups ?? []).map((group) => readsAsPredicate(group, readsAsContext)),
      ];
    });
    return clauseTexts.filter(Boolean).join(' · ');
  }, [byLayer, t, availableProperties, availableRelationships, availablePerspectives, i18n?.language, getPropertyLabel]);

  /**
   * The draft minus its incomplete rows — what a save actually commits. The
   * draft tree keeps every row the author has open; a row with no value yet
   * is excluded from the saved predicate (spec edge case "Incomplete row
   * while authoring"), and a draft holding only such rows has nothing to save.
   */
  const savableDraft = useMemo<UnifiedFilters>(
    () => leavesToUnifiedLanes(pruneIncompleteLeaves({ operator: 'and', expressions: unifiedLanesToLeaves(draft) }).expressions),
    [draft]
  );
  const isDraft = hasAnyFilterLane(savableDraft);

  /**
   * Whether the session tier holds clauses a Save can promote ("changes apply
   * as you go"; Save keeps them for good — context/column-filter.jsx:421-424).
   * `byLayer.session` is already scoped to `focusProperty` in the drill-in.
   * Never under a link: its session layer is suppressed, so there is nothing
   * of the recipient's own on screen to promote.
   */
  const hasSessionToPromote = !linkActive && hasAnyFilterLane(byLayer[AUTHORING_LAYER]);
  const canSave = isDraft || hasSessionToPromote;

  /** A column/level popover — the single-purpose focus drill-ins, never the plain session tree. */
  const isColumnPopover = !!(focusProperty || focusPerspective || focusArc);
  const [shareConfirmOpen, setShareConfirmOpen] = useState(false);
  // Ties the confirm dialog to the "Share with view…" trigger via `aria-controls`
  // (the same anchor pattern a dropdown's trigger uses for its listbox) — without it,
  // the dialog and this popover are two independent portaled overlays with no DOM or
  // anchor relationship, which is exactly the ambiguous focus/z-order a modal is meant
  // to rule out.
  const shareConfirmDialogId = `${scopeId}-share-confirm`;
  const viewSaveTarget = effectiveTargets.find((s) => s.target === 'view');
  const viewShareLockedReason = lockedTargetReason(viewSaveTarget, t);

  // Whether the view or context tier has any group filters (drives AND connector + FilterGroupCard)
  const viewHasGroups = useMemo(() => (byLayer.view.filterGroups?.length ?? 0) > 0, [byLayer.view]);
  const contextHasGroups = useMemo(() => (byLayer.context.filterGroups?.length ?? 0) > 0, [byLayer.context]);

  // Show the AND connector when a group exists in any locked tier AND session has rows (dev note #5)
  const showAndConnector = useMemo(
    () => (viewHasGroups || contextHasGroups) && hasAnyFilterLane(byLayer[AUTHORING_LAYER]),
    [viewHasGroups, contextHasGroups, byLayer]
  );

  // Build locked clauses once — used by both the draft conflict check and the
  // session-row conflict check below.
  const lockedClauses = useMemo((): Array<{ clause: FilterExpression; layer: FilterLayer }> => {
    const result: Array<{ clause: FilterExpression; layer: FilterLayer }> = [];
    DISPLAY_LAYER_ORDER.forEach((layer) => {
      if (!isLayerLocked(layer, effectiveTargets)) return;
      (byLayer[layer].propertyFilters ?? []).forEach((clause) => result.push({ clause, layer }));
    });
    return result;
  }, [byLayer, effectiveTargets]);

  // Same as `lockedClauses` above, but for the perspective/relationship/arc
  // lanes (SC7, Phase 7d) — every non-property lane a locked context/view
  // tier carries, reduced to its comparable `LaneValueClause`s.
  const lockedLaneClauses = useMemo((): Array<{ clause: LaneValueClause; layer: FilterLayer }> => {
    const result: Array<{ clause: LaneValueClause; layer: FilterLayer }> = [];
    DISPLAY_LAYER_ORDER.forEach((layer) => {
      if (!isLayerLocked(layer, effectiveTargets)) return;
      (byLayer[layer].perspectiveFilters ?? []).forEach((filter) =>
        laneValueClauses({ kind: 'perspective', filter }).forEach((clause) => result.push({ clause, layer }))
      );
      (byLayer[layer].relationshipFilters ?? []).forEach((filter) =>
        laneValueClauses({ kind: 'relationship', filter }).forEach((clause) => result.push({ clause, layer }))
      );
      (byLayer[layer].arcFilters ?? []).forEach((filter) =>
        laneValueClauses({ kind: 'arc', filter }).forEach((clause) => result.push({ clause, layer }))
      );
    });
    return result;
  }, [byLayer, effectiveTargets]);

  // Conflict detection for the DRAFT (editor section) — scoped to focusProperty.
  const conflict = useMemo(() => {
    if (!focusProperty) return undefined;
    const draftClause = (draft.propertyFilters ?? []).find((f) => f.property === focusProperty);
    if (!draftClause) return undefined;
    const found = findConflictingClause(draftClause, lockedClauses.map((l) => l.clause));
    if (!found) return undefined;
    const owner = lockedClauses.find((l) => l.clause === found);
    if (!owner) return undefined;
    const value = typeof found.value === 'string' ? getPropertyValueLabel(focusProperty, found.value) : undefined;
    return { property: focusProperty, layer: owner.layer, value };
  }, [draft, focusProperty, lockedClauses, getPropertyValueLabel]);

  // Conflict detection for EXISTING SESSION ROWS (dev notes #1, #2).
  // Check both context and view locked tiers against every session property filter.
  // Returns the first conflict found so the warning banner can name the tier.
  const sessionRowConflict = useMemo((): { property: string; layer: FilterLayer; value?: string } | undefined => {
    const sessionFilters = byLayer[AUTHORING_LAYER].propertyFilters ?? [];
    for (const sessionClause of sessionFilters) {
      const found = findConflictingClause(sessionClause, lockedClauses.map((l) => l.clause));
      if (found) {
        const owner = lockedClauses.find((l) => l.clause === found);
        if (owner) {
          // Names WHAT the locked clause restricts to, not just that it does
          // (context/column-filter.jsx:484-485 "restricts it to <b>{theirs}</b>") —
          // the same treatment the draft-path conflict above already gets.
          const value = typeof found.value === 'string' ? getPropertyValueLabel(sessionClause.property, found.value) : undefined;
          return { property: sessionClause.property, layer: owner.layer, value };
        }
      }
    }
    return undefined;
  }, [byLayer, lockedClauses, getPropertyValueLabel]);

  /**
   * Catalog entry → the leaf the draft tree edits. `mintFilterFromCatalogEntry`
   * owns the per-lane shapes; the aspect/identity arm is re-stamped here with
   * the option's owning `aspectSystemName` (SC11 — a composed property must
   * never be emitted in the bare-aspect form the catalog entry alone can't
   * distinguish from a synthetic property).
   */
  //
  // Every minted leaf also gets its client id here (a lane leaf's on `filter.id`,
  // where lane leaves keep identity): the tree keys rows, drag grips and the
  // "just added → open and focus its value" tracking on it, so a leaf added
  // without one could be neither dragged nor focused.
  const leafFromCatalogEntry = useCallback(
    (entry: CatalogEntry): FilterGroupLeaf => {
      const minted = mintFilterFromCatalogEntry(entry) as FilterGroupLeaf;
      if (isLaneLeaf(minted)) {
        // Rebuilt per discriminant: spreading the union widens `filter` to
        // all three payloads, which no single leaf member accepts back.
        if (minted.kind === 'perspective') return { kind: 'perspective', filter: { ...minted.filter, id: minted.filter.id ?? nextFilterId() } };
        if (minted.kind === 'arc') return { kind: 'arc', filter: { ...minted.filter, id: minted.filter.id ?? nextFilterId() } };
        return { kind: 'relationship', filter: { ...minted.filter, id: minted.filter.id ?? nextFilterId() } };
      }
      const aspect = availableProperties.find((p) => p.key === entry.key)?.aspectSystemName;
      const withId = { ...minted, id: minted.id ?? nextFilterId() };
      return aspect ? { ...withId, aspect } : withId;
    },
    [availableProperties]
  );

  /**
   * An emptied draft tree means the author removed the picked row, so the pick
   * goes with it — otherwise `draftTreeGroup` would re-seed that same row from
   * `pickedProperty`/`pickedLeaf` and the removal would never take. The
   * per-lane drill-in has no picker to return to (its pick IS the popover's
   * lane), so there removing the row closes the draft editor, leaving the
   * session tree's own add as the way back in.
   */
  const handleDraftTreeChange = useCallback(
    (next: FilterGroup) => {
      const leaves = next.expressions ?? [];
      setDraft(leavesToUnifiedLanes(leaves));
      if (leaves.length > 0) return;
      if (focusProperty || focusPerspective || focusArc) {
        closeEditor();
        return;
      }
      setPickedProperty(null);
      setPickedLeaf(null);
    },
    [focusProperty, focusPerspective, focusArc, closeEditor]
  );

  // When pickedProperty is set but the draft has no filter for it yet (fresh
  // add-filter flow), seed the editor value with an empty filter pre-bound to
  // that property so PropertyFilterMolecule skips its "Select aspect..." picker
  // and goes straight to the value editor.
  // Step 1 of the add-filter flow — the same catalog every other authoring
  // surface offers, narrowed to the drilled-in property when the popover was
  // opened per-property rather than per-level.
  //
  // A pick here MINTS a leaf (`leafFromCatalogEntry`), so the perspective and
  // relationship lanes list one row per real perspective / relationship — the
  // `availableProperties` pseudo-options "Perspective" / "Relationship" name a
  // lane, not an edge, and minting one wrote the lane word itself as the
  // perspective / edge label (a relationship row picked in a TreeMap column
  // queried edges labelled `relationship` and emptied the column).
  const addFilterCatalogEntries = useMemo(() => {
    if (focusProperty) {
      return propertyOptionsToCatalogEntries(availableProperties.filter((p) => p.key === focusProperty), t);
    }
    if (focusPerspective) {
      return perspectiveOptionsToCatalogEntries(
        availablePerspectives.filter((p) => p.systemName === focusPerspective),
        t
      );
    }
    if (focusArc) {
      return buildArcCatalogEntries(t, true);
    }
    const aspectOptions = availableProperties.filter((p) => p.type !== 'perspective' && p.type !== 'relationship');
    return orderCatalogEntries([
      ...propertyOptionsToCatalogEntries(aspectOptions, t),
      ...perspectiveOptionsToCatalogEntries(availablePerspectives, t),
      ...relationshipOptionsToCatalogEntries(availableRelationships ?? [], t),
    ]);
  }, [availableProperties, availablePerspectives, availableRelationships, focusProperty, focusPerspective, focusArc, t]);

  /**
   * Step 2's tree value. An untouched pick seeds the one leaf it stands for so
   * the row opens on its value editor rather than an empty tree; once the user
   * edits, `draft` is authoritative. A lane pick carries the catalog's minted
   * leaf; the `focusProperty` drill-in (which never passes through the catalog)
   * seeds the column's own property row, carrying its `aspectSystemName` (SC2).
   */
  const draftTreeGroup = useMemo<FilterGroup>(() => {
    const leaves = unifiedLanesToLeaves(draft);
    if (leaves.length > 0) return { id: DRAFT_GROUP_ID, operator: 'and', expressions: leaves };
    if (!pickedProperty) return { id: DRAFT_GROUP_ID, operator: 'and', expressions: [] };
    if (pickedLeaf) return { id: DRAFT_GROUP_ID, operator: 'and', expressions: [pickedLeaf] };
    const aspect = availableProperties.find((p) => p.key === pickedProperty)?.aspectSystemName;
    return {
      id: DRAFT_GROUP_ID,
      operator: 'and',
      expressions: [{
        id: `${pickedProperty}-draft`,
        property: pickedProperty,
        aspect,
        operator: 'equals' as const,
        value: '',
      }],
    };
  }, [draft, pickedProperty, pickedLeaf, availableProperties]);

  /**
   * Writes chosen while a link was active, waiting for the exit to land
   * (EAINT-793 R-1). Held here rather than composed at click time because the
   * composition base does not exist yet — see `commitSave` and the replay
   * effect below. Every write path of this popover defers through `deferSave`.
   */
  const [pendingSaves, setPendingSaves] = useState<PendingSave[]>([]);

  const deferSave = useCallback(
    (save: PendingSave) => {
      setPendingSaves((queue) => [
        ...queue.filter((pending) => save.source === 'addition' || pending.source !== save.source),
        save,
      ]);
      onExitSharedLink?.();
    },
    [onExitSharedLink]
  );

  const cancelDeferredSave = useCallback((source: PendingSaveSource) => {
    setPendingSaves((queue) => queue.filter((pending) => pending.source !== source));
  }, []);

  // ── Session-layer direct saves (the embedded tree / copy-group) ────────────
  // Placed after byLayerFull so TypeScript sees the declaration before use.
  //
  // everySurfaceMountsTheSameTree (spec § Reuse Audit — the session filter row
  // is superseded here): the session tier's OWN clauses render/edit/remove
  // through one `UnifiedFilterTreeMolecule` mount, not a bespoke per-property
  // row list — the same fold `SegmentPanelMolecule`/`ContextLevelInspectorOrganism`
  // use for their own scope. This also fixes a real gap: session `filterGroups`
  // (added via the advanced editor) previously never rendered in this tier at
  // all — only `propertyFilters` did.
  //
  // `focusProperty` (the per-property drill-in popover) scopes the tree to
  // just that property's own clauses, mirroring `draftTreeGroup`'s narrowing
  // below: groups are excluded (never shown per-property, matching prior
  // behavior) and the write-back below splices the edited clauses back into
  // the FULL session set rather than replacing it, so sibling properties'
  // session clauses are untouched.
  //
  // Every lane the session layer carries becomes a leaf — property rows AND
  // perspective/relationship/arc conditions (`unifiedLanesToLeaves`) — so a
  // lane condition renders in the tree and round-trips through
  // `handleSessionTreeChange`. Building the tree from property rows alone
  // made every write-back (which rewrites every lane from the tree) delete
  // the session's lane conditions on any property-row edit.
  const sessionTreeGroup = useMemo<FilterGroup>(() => {
    // The per-property / per-perspective drill-in edits only that one lane's
    // own rows; its write-back splices that lane's rows only, so groups and
    // every other lane's conditions stay out of it (mirrors the property path
    // below for `focusPerspective`, W5).
    if (focusPerspective) {
      const scopedPerspectiveFilters = (byLayerFull[AUTHORING_LAYER].perspectiveFilters ?? []).filter(
        (f) => f.perspectiveSystemName === focusPerspective
      );
      const leaves: FilterGroupLeaf[] = unifiedLanesToLeaves({
        perspectiveFilters: withStableIds(scopedPerspectiveFilters),
      });
      return { id: nextFilterId(), operator: 'and', expressions: leaves };
    }
    if (focusArc) {
      const scopedArcFilters = narrowToArc(byLayerFull[AUTHORING_LAYER], focusArc).arcFilters ?? [];
      const leaves: FilterGroupLeaf[] = unifiedLanesToLeaves({
        arcFilters: withStableIds(scopedArcFilters),
      });
      return { id: nextFilterId(), operator: 'and', expressions: leaves };
    }
    const scopedPropertyFilters = focusProperty
      ? (byLayerFull[AUTHORING_LAYER].propertyFilters ?? []).filter((f) => f.property === focusProperty)
      : (byLayerFull[AUTHORING_LAYER].propertyFilters ?? []);
    const scopedGroups = focusProperty ? [] : (byLayerFull[AUTHORING_LAYER].filterGroups ?? []);
    // The per-property drill-in edits only that property's own rows; its
    // write-back splices property rows only, so lane conditions stay out of it.
    const leaves: FilterGroupLeaf[] = focusProperty
      ? withStableIds(scopedPropertyFilters)
      : unifiedLanesToLeaves({ ...byLayerFull[AUTHORING_LAYER], propertyFilters: withStableIds(scopedPropertyFilters) });
    if (leaves.length === 0) {
      if (scopedGroups.length === 1) return scopedGroups[0];
      return { id: nextFilterId(), operator: 'and', expressions: [], groups: scopedGroups.length > 0 ? scopedGroups : undefined };
    }
    if (scopedGroups.length === 0) {
      return { id: nextFilterId(), operator: 'and', expressions: leaves };
    }
    if (scopedGroups[0].operator === 'and') {
      const merged = scopedGroups.map((g, i) =>
        i === 0 ? { ...g, expressions: [...g.expressions, ...leaves] } : g
      );
      return merged.length === 1 ? merged[0] : { id: nextFilterId(), operator: 'and', expressions: [], groups: merged };
    }
    const leafGroup: FilterGroup = { id: nextFilterId(), operator: 'and', expressions: leaves };
    return { id: nextFilterId(), operator: 'and', expressions: [], groups: [leafGroup, ...scopedGroups] };
  }, [focusProperty, focusPerspective, focusArc, byLayerFull]);

  /**
   * The session tree as the author is editing it — including rows still
   * incomplete (no value yet, `in []`), which the persisted session layer
   * deliberately does NOT carry (spec edge case "Incomplete row while
   * authoring — excluded from the predicate and the runtime body").
   * `basisKey` is the persisted clause content this tree was saved as; while
   * the session layer still holds exactly that, this tree is what renders.
   * Once anything else changes the session layer (Reset mine, copy a group,
   * the advanced editor, another surface), the derived `sessionTreeGroup`
   * takes over and this is dropped.
   */
  const [sessionAuthoring, setSessionAuthoring] = useState<{ tree: FilterGroup; basisKey: string } | null>(null);
  const persistedSessionKey = useMemo(() => layerClauseKey(byLayerFull[AUTHORING_LAYER]), [byLayerFull]);
  const authoringTree = currentAuthoringTree(sessionAuthoring, persistedSessionKey);
  const sessionAuthoringCurrent = authoringTree !== null;
  useEffect(() => {
    if (sessionAuthoring && !sessionAuthoringCurrent) setSessionAuthoring(null);
  }, [sessionAuthoring, sessionAuthoringCurrent]);
  const sessionTreeValue = authoringTree ?? sessionTreeGroup;
  const sessionTreeHasRows =
    (sessionTreeValue.expressions?.length ?? 0) > 0 || (sessionTreeValue.groups?.length ?? 0) > 0;
  // An incomplete session row is authoring too — it folds the inherited tiers
  // exactly as a persisted one does (spec: "still triggers the tier fold"),
  // even though the persisted layer (`hasSessionFilter`) does not carry it.
  useEffect(() => {
    if (sessionTreeHasRows) setInheritedFolded(true);
  }, [sessionTreeHasRows]);

  // The mount-time counterpart of the trigger's `onToggle` reset below: a
  // focusProperty/focusPerspective/focusArc column's draft editor needs
  // `sessionTreeHasRows` (not available until this point in render) to know
  // whether to open on the value input or stay collapsed to the tree's own
  // rows, so the very first open can't be decided by the `useState`
  // initializers above. `useLayoutEffect`, not `useEffect`, so it lands before
  // paint — the popover is open from the first frame, no window for real
  // interaction to race a correction that arrives later.
  useLayoutEffect(() => {
    if (focusProperty || focusPerspective || focusArc) {
      setShowEditor(!sessionTreeHasRows);
      setPickedProperty(focusProperty ?? focusPerspective ?? focusArc ?? null);
      setPickedLeaf(mintFocusLeaf());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A focused row (single property or perspective) narrows the session tree's
  // own "Add" catalog to just that field — there is nothing else to add.
  const sessionAvailableProperties = useMemo(() => {
    if (focusProperty) return availableProperties.filter((p) => p.key === focusProperty);
    if (focusPerspective || focusArc) return [];
    return availableProperties;
  }, [focusProperty, focusPerspective, focusArc, availableProperties]);
  const sessionAvailablePerspectives = useMemo(() => {
    if (focusPerspective) return availablePerspectives.filter((p) => p.systemName === focusPerspective);
    if (focusArc) return [];
    return availablePerspectives;
  }, [focusPerspective, focusArc, availablePerspectives]);

  // Per-row problems for the session tree (spec Phase 4: "Per-row problems
  // wired in every host with a locked tier") — keyed by leaf id so
  // `UnifiedFilterTreeMolecule` can mark the exact row that contradicts a
  // locked context/view clause, instead of the single panel-level notice
  // `sessionRowConflict` above already renders.
  const sessionProblemsByLeaf = useMemo(
    () =>
      findConflictsByLeaf(
        sessionTreeValue,
        lockedClauses.map((l) => ({ clause: l.clause, tier: l.layer })),
        lockedLaneClauses.map((l) => ({ clause: l.clause, tier: l.layer }))
      ),
    [sessionTreeValue, lockedClauses, lockedLaneClauses]
  );

  const problemOf = useCallback(
    (leafId: string) => {
      const owner = sessionProblemsByLeaf.get(leafId);
      if (!owner) return undefined;
      const tier = t(CONFLICT_TIER_KEY[owner.tier]);
      // A lane clause (perspective/relationship/arc) has no single scalar
      // value worth naming — it names a target, not a property value — so
      // only a property clause's `problemOf` message includes the value.
      if (!('property' in owner.clause)) {
        return { title: t('columnFilters.conflict.rowMessage', { tier }), message: t('columnFilters.conflict.rowMessage', { tier }) };
      }
      const value = typeof owner.clause.value === 'string'
        ? getPropertyValueLabel(owner.clause.property, owner.clause.value)
        : undefined;
      const message = value
        ? t('columnFilters.conflict.rowMessageWithValue', { tier, value })
        : t('columnFilters.conflict.rowMessage', { tier });
      return { title: message, message };
    },
    [sessionProblemsByLeaf, t, getPropertyValueLabel]
  );

  /**
   * The session layer a tree persists as. Every lane is written explicitly —
   * the session merge (`deepMergeJson`) keeps any key a patch omits, so an
   * omitted lane would survive beside the same condition now held in the tree.
   */
  //
  // `base` is the session layer the tree is spliced into — the persisted one,
  // or `EMPTY_LAYER` for just what the tree itself holds (a write deferred
  // behind a shared-link exit, which is added to the restored layer instead).
  const sessionLayerFromTree = useCallback(
    (tree: FilterGroup, base: UnifiedFilters = byLayerFull[AUTHORING_LAYER]): UnifiedFilters => {
      if (focusProperty) {
        const scopedClauses = (tree.expressions ?? []) as FilterExpression[];
        const others = (base.propertyFilters ?? []).filter((f) => f.property !== focusProperty);
        return { ...base, propertyFilters: [...others, ...scopedClauses] };
      }
      if (focusPerspective) {
        const scopedClauses = leavesToUnifiedLanes((tree.expressions ?? []) as FilterGroupLeaf[]).perspectiveFilters ?? [];
        const others = (base.perspectiveFilters ?? []).filter(
          (f) => f.perspectiveSystemName !== focusPerspective
        );
        return { ...base, perspectiveFilters: [...others, ...scopedClauses] };
      }
      if (focusArc) {
        const scopedClauses = leavesToUnifiedLanes((tree.expressions ?? []) as FilterGroupLeaf[]).arcFilters ?? [];
        const others = (base.arcFilters ?? []).filter((f) => !isFocusedArcFilter(f, focusArc));
        return { ...base, arcFilters: [...others, ...scopedClauses] };
      }
      // A root AND with no nested groups round-trips as the flat per-lane
      // arrays the session tier stored before this tree existed — a group
      // wrapper only earns its keep for `or` (whose semantics a flat list
      // can't express) or when the author actually nested a group.
      const isFlatAnd = tree.operator === 'and' && (tree.groups?.length ?? 0) === 0;
      if (isFlatAnd) {
        const lanes = leavesToUnifiedLanes(tree.expressions ?? []);
        return {
          ...base,
          propertyFilters: lanes.propertyFilters ?? [],
          perspectiveFilters: lanes.perspectiveFilters ?? [],
          relationshipFilters: lanes.relationshipFilters ?? [],
          arcFilters: lanes.arcFilters ?? [],
          filterGroups: [],
        };
      }
      return {
        ...base,
        propertyFilters: [],
        perspectiveFilters: [],
        relationshipFilters: [],
        arcFilters: [],
        filterGroups: [tree],
      };
    },
    [focusProperty, focusPerspective, focusArc, byLayerFull]
  );

  // The authoring tree keeps every row; only its complete rows are persisted
  // (and so reach the runtime body). A change that only touches incomplete
  // rows — adding one, typing into one that is still incomplete — leaves the
  // persisted layer as it is, so nothing is re-saved or re-queried for it.
  //
  // Under a link the tier on screen is the suppressed (empty) session, so all
  // the tree holds was authored here: it is deferred and ADDED to the
  // recipient's own session once the exit lands. The tree stays on screen
  // (its basis is the layer currently shown) until then.
  const handleSessionTreeChange = useCallback(
    (next: FilterGroup) => {
      if (sessionLocked) return;
      if (linkActive) {
        setSessionAuthoring({ tree: next, basisKey: persistedSessionKey });
        const authored = sessionLayerFromTree(pruneIncompleteLeaves(next), EMPTY_LAYER);
        if (hasAnyFilterLane(authored)) {
          deferSave({ target: AUTHORING_TARGET, clauses: authored, source: 'sessionAuthoring' });
        } else {
          cancelDeferredSave('sessionAuthoring');
        }
        return;
      }
      const prevPersisted = byLayerFull[AUTHORING_LAYER];
      const persisted = sessionLayerFromTree(pruneIncompleteLeaves(next));
      const basisKey = layerClauseKey(persisted);
      setSessionAuthoring({ tree: next, basisKey });
      if (basisKey !== persistedSessionKey) {
        setRemovedUndo(countClauses(persisted) < countClauses(prevPersisted) ? { layer: prevPersisted } : null);
        onSave(persisted, AUTHORING_TARGET);
      }
    },
    [sessionLocked, linkActive, sessionLayerFromTree, persistedSessionKey, byLayerFull, onSave, deferSave, cancelDeferredSave]
  );

  /** Restores the layer captured just before the last row removal and re-saves it. */
  const handleUndoRemove = useCallback(() => {
    if (!removedUndo) return;
    onSave(removedUndo.layer, AUTHORING_TARGET);
    setRemovedUndo(null);
  }, [removedUndo, onSave]);

  // Every host of the session tree is narrow, so "Add condition" drills the
  // catalog in below the tree (`UnifiedFilterTreeMolecule`'s drill-in), which
  // renders nothing without a catalog to drill into.
  const renderSessionCatalog = useCallback(
    ({ onPick, onClose }: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => (
      <AspectCatalogMolecule
        entries={addFilterCatalogEntries}
        loading={propertiesLoading}
        error={propertiesError}
        onPick={(entry) => {
          onPick(leafFromCatalogEntry(entry));
          onClose();
        }}
        onCancel={onClose}
        emptyScopeHint={scopeNote}
        scopeTypeLabels={scopeTypeLabels}
        inline
        testIdPrefix="level-filter-session-catalog"
      />
    ),
    [addFilterCatalogEntries, propertiesLoading, propertiesError, leafFromCatalogEntry, scopeNote, scopeTypeLabels]
  );

  const handleCopyGroupToSession = useCallback(
    (group: FilterGroup) => {
      if (sessionLocked) return;
      if (linkActive) {
        deferSave({ target: AUTHORING_TARGET, clauses: { filterGroups: [group] }, source: 'addition' });
        return;
      }
      const existing = byLayerFull[AUTHORING_LAYER].filterGroups ?? [];
      onSave({ ...byLayerFull[AUTHORING_LAYER], filterGroups: [...existing, group] }, AUTHORING_TARGET);
    },
    [sessionLocked, linkActive, deferSave, byLayerFull, onSave]
  );

  /**
   * Removes one group from the VIEW layer directly — the editable
   * counterpart to `handleCopyGroupToSession`, offered on a view group's
   * card only once `!isLayerLocked('view', ...)` (`canEditSharedView`, or the
   * group is the caller's own). Matches the prototype's inline
   * `setViewFilters(xs => xs.filter(...))` (`context/column-filter.jsx:406`).
   */
  const handleRemoveViewGroup = useCallback(
    (index: number) => {
      const existing = byLayerFull.view.filterGroups ?? [];
      onSave({ ...byLayerFull.view, filterGroups: existing.filter((_, i) => i !== index) }, 'view');
    },
    [byLayerFull.view, onSave]
  );

  /**
   * Remove/update one VIEW-layer property filter directly — the editable
   * counterpart of the session-layer handlers above, gated the same way as
   * `handleRemoveViewGroup` (only reachable once `!isLayerLocked('view', ...)`).
   */
  //
  // Rows are addressed by filter id, not position: the rows render the
  // focus-narrowed `viewRowFilters`, whose positions do not line up with the
  // full layer written back here. The written layer keeps the ids, as the
  // session tree's write-back does, so a row's id stays fixed across saves.
  const handleRemoveViewPropertyFilter = useCallback(
    (filterId: string) => {
      const next = viewPropertyFiltersWithIds.filter((f) => f.id !== filterId);
      onSave({ ...byLayerFull.view, propertyFilters: next }, 'view');
    },
    [viewPropertyFiltersWithIds, byLayerFull.view, onSave]
  );

  const handleUpdateViewPropertyFilter = useCallback(
    (filterId: string, updated: FilterExpression) => {
      const next = viewPropertyFiltersWithIds.map((f) => (f.id === filterId ? { ...updated, id: filterId } : f));
      onSave({ ...byLayerFull.view, propertyFilters: next }, 'view');
    },
    [viewPropertyFiltersWithIds, byLayerFull.view, onSave]
  );

  const commitSave = useCallback(
    (target: FilterSaveTarget, clauses: UnifiedFilters) => {
      // `onSave` REPLACES this level's clauses for `target` — both merge
      // implementations (`deepMergeJson`, Postgres `jsonb_deep_merge`) replace
      // arrays rather than appending, so a bare `draft` would wipe whatever the
      // user had already saved to that layer. The groups above are read-only,
      // so the editor is purely additive: send the layer's existing clauses
      // concatenated with the draft. Never-widen still holds — this only ever
      // grows the layer's clause set.
      return onSave(concatUnifiedFilters(byLayerFull[target], clauses) ?? clauses, target);
    },
    [onSave, byLayerFull]
  );

  /** 5xx/network/unknown are transient — the same write can succeed on retry. A 403 or 400 will not. */
  const classifySaveError = useCallback((err: unknown): boolean => {
    const status =
      (err as { status?: number })?.status ?? (err as { response?: { status?: number } })?.response?.status;
    return status !== 403 && status !== 400;
  }, []);

  /**
   * Wraps `commitSave` so a host that returns a real promise (Phase 5) drives
   * the save-failure footer; a fire-and-forget host (today's `void` return)
   * runs `onSuccess` synchronously, unchanged from before this existed.
   */
  const attemptSave = useCallback(
    (target: FilterSaveTarget, clauses: UnifiedFilters, onSuccess: () => void) => {
      lastSaveAttemptRef.current = { target, clauses, onSuccess };
      setSaveError(null);
      const result = commitSave(target, clauses);
      if (result && typeof (result as Promise<void>).then === 'function') {
        (result as Promise<void>).then(onSuccess, (err: unknown) => {
          setSaveError({ target, retryable: classifySaveError(err) });
        });
        return;
      }
      onSuccess();
    },
    [commitSave, classifySaveError]
  );

  const retrySave = useCallback(() => {
    const attempt = lastSaveAttemptRef.current;
    if (!attempt) return;
    attemptSave(attempt.target, attempt.clauses, attempt.onSuccess);
  }, [attemptSave]);

  /**
   * "Save is not apply" — the popover stays open after saving (dev note). A
   * ~2.6s confirmation names where the clauses went (badge flips SESSION → IN VIEW).
   */
  const confirmPromotion = useCallback((target: FilterSaveTarget) => {
    if (promotionTimerRef.current) clearTimeout(promotionTimerRef.current);
    setPromotedTarget(target);
    promotionTimerRef.current = setTimeout(() => setPromotedTarget(null), 2600);
  }, []);

  /**
   * Save with no draft open promotes the session tier: its clauses already
   * apply, so Save moves them to the chosen layer and out of the session, where
   * they would otherwise apply twice and show in two tiers
   * (context/column-filter.jsx:423-424). "Keep for this session" has nothing
   * to write — the clauses are already there.
   */
  const promoteSession = useCallback(
    (target: FilterSaveTarget) => {
      if (target !== AUTHORING_TARGET) {
        attemptSave(target, byLayer[AUTHORING_LAYER], () => {
          const remaining = sessionLayerAfterPromotion(
            byLayerFull[AUTHORING_LAYER],
            focusProperty,
            focusPerspective,
            focusArc
          );
          onSave(remaining, AUTHORING_TARGET);
          confirmPromotion(target);
        });
        return;
      }
      confirmPromotion(target);
    },
    [attemptSave, byLayer, focusProperty, focusPerspective, focusArc, byLayerFull, onSave, confirmPromotion]
  );

  /**
   * Confirmed "Share with view…" — hands this column's own user-tier clauses
   * to the host (which writes them to the view) and, once that resolves,
   * removes the local copies the same way `promoteSession` does (OQ-1: move,
   * not copy — the removal happens only after the write succeeds).
   */
  const attemptShare = useCallback(
    (clauses: UnifiedFilters) => {
      setShareError(null);
      Promise.resolve(onShareWithView?.(clauses)).then(
        () => {
          const remaining = sessionLayerAfterPromotion(
            byLayerFull[AUTHORING_LAYER],
            focusProperty,
            focusPerspective,
            focusArc
          );
          onSave(remaining, AUTHORING_TARGET);
          confirmPromotion('view');
          let lane = 'level';
          if (focusProperty) lane = 'property';
          else if (focusPerspective) lane = 'perspective';
          else if (focusArc) lane = 'arc';
          logAnalyticsEvent('column_filter_shared_with_view', undefined, {
            host: scopeId,
            lane,
            count: countClauses(clauses),
          });
        },
        (err: unknown) => {
          const status =
            (err as { status?: number })?.status ?? (err as { response?: { status?: number } })?.response?.status;
          // The buffer is retained by construction — nothing above cleared it,
          // so the user-tier clauses are still applied after any failure.
          if (status === 403) onViewPermissionDenied?.();
          setShareError({ retryable: classifySaveError(err) });
        }
      );
    },
    [
      byLayerFull,
      onShareWithView,
      onSave,
      focusProperty,
      focusPerspective,
      focusArc,
      confirmPromotion,
      onViewPermissionDenied,
      classifySaveError,
      logAnalyticsEvent,
      scopeId,
    ]
  );

  const handleConfirmShare = useCallback(() => {
    setShareConfirmOpen(false);
    attemptShare(byLayer[AUTHORING_LAYER]);
  }, [byLayer, attemptShare]);

  const retryShare = useCallback(() => {
    attemptShare(byLayer[AUTHORING_LAYER]);
  }, [byLayer, attemptShare]);

  const handleSave = useCallback(
    (target: FilterSaveTarget) => {
      if (effectiveTargets.find((s) => s.target === target)?.disabled) return;

      if (!hasAnyFilterLane(savableDraft)) {
        if (hasSessionToPromote) promoteSession(target);
        return;
      }

      // The never-widen constraint holds because commitSave concatenates onto
      // the existing layer rather than replacing it.
      if (linkActive) {
        // Store the resolved clauses so the deferred commitSave (effect below)
        // composes against the restored base, not the suppressed one.
        deferSave({ target, clauses: savableDraft, source: 'draft', confirms: true });
        return;
      }

      attemptSave(target, savableDraft, () => {
        setDraft({});
        closeEditor();
        confirmPromotion(target);
      });
    },
    [
      savableDraft,
      hasSessionToPromote,
      promoteSession,
      linkActive,
      deferSave,
      attemptSave,
      effectiveTargets,
      closeEditor,
      confirmPromotion,
    ]
  );

  // `linkActive` and `composed` are two views of ONE `useLevelFilters` render:
  // `saveTargets` loses `link_exits_first` in the same commit that restores
  // `byLevel`'s suppressed layers. So the first render on which this fires is
  // also the first on which `byLayerFull` holds the recipient's real clauses —
  // never a same-tick read of stale state.
  //
  // Pending writes to the same target are folded into one addition first:
  // `commitSave` composes against this render's `byLayerFull`, so two separate
  // writes to one target would each start from the same base and the second
  // would drop the first.
  useEffect(() => {
    if (pendingSaves.length === 0 || linkActive) return;
    const additions = new Map<FilterSaveTarget, UnifiedFilters>();
    pendingSaves.forEach(({ target, clauses }) => {
      const sofar = additions.get(target);
      additions.set(target, sofar ? concatUnifiedFilters(sofar, clauses) ?? sofar : clauses);
    });
    additions.forEach((clauses, target) => commitSave(target, clauses));
    if (pendingSaves.some((pending) => pending.source === 'draft')) {
      setDraft({});
      closeEditor();
    }
    if (pendingSaves.some((pending) => pending.closesAdvancedEditor)) setAdvancedEditorState(null);
    const confirmed = [...pendingSaves].reverse().find((pending) => pending.confirms);
    if (confirmed) confirmPromotion(confirmed.target);
    setPendingSaves([]);
  }, [pendingSaves, linkActive, commitSave, closeEditor, confirmPromotion]);

  /**
   * A column popover (Atlas column or TreeMap level) has no draft + Save
   * pairing and no save-target menu (BLOCKER 1, F4/OQ-1): every change commits
   * to the user tier — or defers under a link, same as any other write here —
   * the moment the draft prunes to a complete leaf, and the draft closes right
   * after. The non-focus session tree is unaffected: its own rows commit
   * through `handleSessionTreeChange`, not this draft/`savableDraft` path.
   *
   * The already-deferred check guards against double-committing: once
   * `deferSave` lands (either because a link was never active, or because
   * `onExitSharedLink` resolved it), `linkActive` flips false in the SAME
   * flush this effect runs in — without the guard it would see the
   * still-complete `savableDraft` again and commit it a second time, on top
   * of the replay effect (`pendingSaves`, below) that already does. Only a
   * genuinely NEW value (one that doesn't match what's already queued) is
   * allowed through while still deferred, so changing your mind before the
   * exit lands replaces the pending write rather than being swallowed by
   * this guard.
   */
  useEffect(() => {
    if (!isColumnPopover || !isDraft) return;
    const alreadyPending = pendingSaves.find((pending) => pending.source === 'draft');
    if (alreadyPending && JSON.stringify(alreadyPending.clauses) === JSON.stringify(savableDraft)) return;
    if (linkActive) {
      deferSave({ target: AUTHORING_TARGET, clauses: savableDraft, source: 'draft', confirms: true });
      return;
    }
    commitSave(AUTHORING_TARGET, savableDraft);
    setDraft({});
    closeEditor();
    confirmPromotion(AUTHORING_TARGET);
  }, [isColumnPopover, isDraft, savableDraft, linkActive, pendingSaves, deferSave, commitSave, closeEditor, confirmPromotion]);

  /**
   * A group-card / new-group Advanced editor result, saved to `target`. Editing
   * an existing group and saving back to the SAME tier it came from replaces it
   * in place; anything else is added. Under a link it is deferred as an
   * addition — a group index only exists for a group this popover rendered from
   * an editable layer, and every such layer is suppressed while a link is
   * active, so there is nothing on screen to replace.
   */
  const applyGroupResult = useCallback(
    (updatedFilter: FilterState, target: FilterSaveTarget) => {
      const groups = (updatedFilter.filterGroups ?? []).map(simpleGroupToUnified);
      if (groups.length === 0) {
        setAdvancedEditorState(null);
        return;
      }
      if (linkActive) {
        deferSave({ target, clauses: { filterGroups: groups }, source: 'addition', closesAdvancedEditor: true });
        return;
      }
      const provenance = advancedEditorState?.provenance;
      const groupIndex = advancedEditorState?.groupIndex;
      const existing = byLayerFull[target]?.filterGroups ?? [];
      // Escalation carries the tree: appending unconditionally duplicated an
      // edited group — its leaves rendered twice, once from the original entry
      // and once from the "edited" copy tacked on the end.
      const next =
        provenance === target && groupIndex !== undefined
          ? existing.map((g, i) => (i === groupIndex ? groups[0] : g))
          : [...existing, ...groups];
      onSave({ ...byLayerFull[target], filterGroups: next }, target);
      setAdvancedEditorState(null);
    },
    [linkActive, deferSave, advancedEditorState, byLayerFull, onSave]
  );

  const isTargetDisabled = useCallback(
    (target: FilterSaveTarget) => effectiveTargets.find((s) => s.target === target)?.disabled === true,
    [effectiveTargets]
  );

  // Tier 1 · Context — locked (note #3). Extracted so its own row/group
  // nesting (view-wide `contextTierRows` vs. the flat per-level fallback)
  // doesn't add to the component body's cognitive complexity.
  const renderContextTier = () =>
    hasAnyFilterLane(byLayer.context) && (
      <div className="level-filter-popover__tier">
        <div className="level-filter-popover__tier-header">
          <span
            className="level-filter-popover__tier-label"
            data-datum="uf-column.heading"
            data-kind="heading"
          >
            <i className="pi pi-lock level-filter-popover__tier-icon" aria-hidden="true" />
            {t('columnFilters.provenance.context.heading')}
            {contextCount > 0 && (
              <span className="level-filter-popover__tier-count">{contextCount}</span>
            )}
          </span>
          {canEditContext && onEditInContext && (
            <button
              type="button"
              className="level-filter-popover__tier-action"
              onClick={onEditInContext}
            >
              <i className="pi pi-external-link" aria-hidden="true" />
              {t('columnFilters.editInContext')}
            </button>
          )}
          {hasInheritedFilters && (
            <button
              type="button"
              className="level-filter-popover__tier-collapse"
              onClick={() => setInheritedFolded(true)}
              aria-label={t('columnFilters.collapse', 'Collapse')}
              title={t('columnFilters.collapse', 'Collapse')}
            >
              <i className="pi pi-chevron-up" aria-hidden="true" />
            </button>
          )}
        </div>
        {/* View-wide scope (`contextTierRows`, W6): one group per level
            that has a clause on this aspect, labelled by level — there is
            no single `context` value to show, unlike a per-level scope's
            flat `byLayer.context` below. */}
        {contextTierRows && contextTierRows.length > 0
          ? contextTierRows.map((row) => {
              const levelLabel =
                row.label?.translations?.[i18n.language] || row.label?.default || row.levelId;
              return (
                <React.Fragment key={row.levelId}>
                  {hasAnyFilterLane({ ...row.filters, filterGroups: [] }) && (
                    <FilterProvenanceGroupMolecule
                      layer="context"
                      filters={{ ...row.filters, filterGroups: [] }}
                      locked
                      scopeNote={levelLabel}
                      getPropertyLabel={getPropertyLabel}
                      getPerspectiveLabel={getPerspectiveLabel}
                      getRelationshipLabel={getRelationshipLabel}
                      getPropertyValueLabel={getPropertyValueLabel}
                      pillDatumId="uf-column.pill#2"
                    />
                  )}
                  {(row.filters.filterGroups ?? []).map((group, i) => (
                    <FilterGroupCardMolecule
                      key={group.id ?? i}
                      group={group}
                      provenance="context"
                      locked
                      lockedReason={t('columnFilters.group.contextLockedReason')}
                      onView={() => openAdvancedEditorForGroup(group, 'context', false)}
                      onCopyToOwn={sessionLocked ? undefined : () => handleCopyGroupToSession(group)}
                      availableProperties={availableProperties}
                      availableRelationships={availableRelationships}
                      availablePerspectives={availablePerspectives}
                      pillDatumId="uf-column.pill"
                      viewDatumId="uf-column.view"
                      copyToOwnDatumId="uf-column.copy-to-my-own"
                    />
                  ))}
                </React.Fragment>
              );
            })
          : (
            <>
              {/* Flat property filters */}
              {hasAnyFilterLane({ ...byLayer.context, filterGroups: [] }) && (
                <FilterProvenanceGroupMolecule
                  layer="context"
                  filters={{ ...byLayer.context, filterGroups: [] }}
                  locked
                  showHeading={false}
                  getPropertyLabel={getPropertyLabel}
                  getPerspectiveLabel={getPerspectiveLabel}
                  getRelationshipLabel={getRelationshipLabel}
                  getPropertyValueLabel={getPropertyValueLabel}
                  pillDatumId="uf-column.pill#2"
                />
              )}
              {/* Group cards (dev note #1 — atomic, never flattened). Always
                  locked (OQ-9): the context tier is never this popover's own
                  to save to — "Edit in context" above is the real edit route,
                  so the group card only ever offers View + Copy to my own. */}
              {(byLayer.context.filterGroups ?? []).map((group, i) => (
                <FilterGroupCardMolecule
                  key={group.id ?? i}
                  group={group}
                  provenance="context"
                  locked
                  lockedReason={t('columnFilters.group.contextLockedReason')}
                  onView={() => openAdvancedEditorForGroup(group, 'context', false)}
                  onCopyToOwn={sessionLocked ? undefined : () => handleCopyGroupToSession(group)}
                  availableProperties={availableProperties}
                  availableRelationships={availableRelationships}
                  availablePerspectives={availablePerspectives}
                  pillDatumId="uf-column.pill"
                  viewDatumId="uf-column.view"
                  copyToOwnDatumId="uf-column.copy-to-my-own"
                />
              ))}
            </>
          )}
        {/* Never-widen notice (dev note #4) */}
        <p className="level-filter-popover__never-widen">
          {t('columnFilters.neverWiden')}
        </p>
      </div>
    );

  // Tier 2 · View — per-row ownership (note #5). Extracted for the same
  // reason as renderContextTier: its locked/editable branch and group-card
  // map each nest independently of the component body.
  const renderViewTier = () =>
    hasAnyFilterLane(byLayer.view) && (
      <div className="level-filter-popover__tier">
        <div className="level-filter-popover__tier-header">
          <span
            className="level-filter-popover__tier-label"
            data-datum="uf-column.heading#2"
            data-kind="heading"
          >
            <i className="pi pi-bookmark level-filter-popover__tier-icon" aria-hidden="true" />
            {t('columnFilters.provenance.view.heading')}
            {viewCount > 0 && (
              <span className="level-filter-popover__tier-count">{viewCount}</span>
            )}
          </span>
          <span className="level-filter-popover__tier-hint">
            {t('columnFilters.sharedRowsReadOnly')}
          </span>
          <button
            type="button"
            className="level-filter-popover__tier-collapse"
            onClick={() => setInheritedFolded(true)}
            aria-label={t('columnFilters.collapse', 'Collapse')}
            title={t('columnFilters.collapse', 'Collapse')}
          >
            <i className="pi pi-chevron-up" aria-hidden="true" />
          </button>
        </div>
        {/* Flat property filters. Locked: read-only chips, same as every
            other tier. Editable (`canEditSharedView`, or the caller
            owns this row): full `<EditableFilterRow>` rows in place —
            this popover can save to `view` directly (`uf-column.value#2`/
            `.button`/`.button#2` closed, `.property`/`.condition`/`.op`/
            `.value#3`/`.apply` once opened), matching the group cards'
            own editable/locked split above. */}
        {hasAnyFilterLane({ ...byLayer.view, filterGroups: [] }) && (
          isLayerLocked('view', effectiveTargets) ? (
            <FilterProvenanceGroupMolecule
              layer="view"
              filters={{ ...byLayer.view, filterGroups: [] }}
              locked
              showHeading={false}
              getPropertyLabel={getPropertyLabel}
              getPerspectiveLabel={getPerspectiveLabel}
              getRelationshipLabel={getRelationshipLabel}
              getPropertyValueLabel={getPropertyValueLabel}
              pillDatumId="uf-column.pill#4"
            />
          ) : (
            <FilterProvenanceGroupMolecule
              layer="view"
              filters={viewRowFilters}
              locked={false}
              showHeading={false}
              getPropertyLabel={getPropertyLabel}
              getPerspectiveLabel={getPerspectiveLabel}
              getRelationshipLabel={getRelationshipLabel}
              getPropertyValueLabel={getPropertyValueLabel}
              variant="rows"
              rowsProps={{
                availableProperties,
                onUpdate: handleUpdateViewPropertyFilter,
                onRemove: handleRemoveViewPropertyFilter,
                arcTemplates,
                availableRelationships,
                familyTypeSchemas,
                availablePerspectives,
                perspectiveSearchProps,
              }}
            />
          )
        )}
        {/* Group cards. Locked (no `canEditSharedView`, not the caller's
            own): View + Copy to my own — same as context. Editable
            (`canEditSharedView`, or the caller owns this row): Edit +
            Remove in place, matching the prototype's `canEdit` branch
            (`context/column-filter.jsx:126-131`) — this popover can save
            to `view` directly, so there is no reason to route through a
            read-only detour. */}
        {(byLayer.view.filterGroups ?? []).map((group, i) => {
          const viewLocked = isLayerLocked('view', effectiveTargets);
          return (
            <FilterGroupCardMolecule
              key={group.id ?? i}
              group={group}
              provenance="view"
              locked={viewLocked}
              lockedReason={viewLocked ? t('columnFilters.group.viewLockedReason') : undefined}
              onView={viewLocked ? () => openAdvancedEditorForGroup(group, 'view', false) : undefined}
              onEdit={!viewLocked ? () => openAdvancedEditorForGroup(group, 'view', true, i) : undefined}
              onRemove={!viewLocked ? () => handleRemoveViewGroup(i) : undefined}
              onCopyToOwn={viewLocked && !sessionLocked ? () => handleCopyGroupToSession(group) : undefined}
              viewName={activeViewDisplayName}
              availableProperties={availableProperties}
              availableRelationships={availableRelationships}
              availablePerspectives={availablePerspectives}
              pillDatumId="uf-column.pill#3"
              viewDatumId="uf-column.view#2"
              editDatumId="uf-column.edit#2"
              removeDatumId="uf-column.remove-this-group#2"
              copyToOwnDatumId="uf-column.copy-to-my-own#2"
            />
          );
        })}
      </div>
    );

  // The panel body — the whole surface, independent of what is holding it: this
  // organism's own overlay (the default) or the host's layer (`trigger={false}`).
  const panelBody = (
      <div
        className="level-filter-popover__content"
        data-container="ui-column"
        ref={contentRef}
        style={bodyMaxHeight != null ? { maxHeight: `${bodyMaxHeight}px`, overflowY: 'auto' } : undefined}
      >

        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="level-filter-popover__header">
          <span className="level-filter-popover__header-title">
            <i className="pi pi-filter" aria-hidden="true" />
            {t('columnFilters.headerTitle', { column: triggerLabel })}
          </span>
          {activeFilterCount > 0 && (
            <span className="level-filter-popover__header-count">
              {t('columnFilters.constraintCount', { count: activeFilterCount })}
            </span>
          )}
          <button
            type="button"
            className="level-filter-popover__header-close"
            // With no overlay of our own (`trigger={false}`) there is nothing here to
            // hide — the host owns the layer, so Close is reported to it instead.
            onClick={() => (trigger ? overlayRef.current?.hide() : onClose?.())}
            aria-label={t('columnFilters.close', 'Close')}
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        </div>

        {/* ── Scope strip (note #2) ─────────────────────────────────────── */}
        {scopeNote && (
          <div className="level-filter-popover__scope-strip">
            <i className="pi pi-info-circle" aria-hidden="true" />
            {scopeNote}
          </div>
        )}

        {/* `noResults` ("No items match the current filters") is the wrong claim here —
            a count of zero means no constraint was ever applied, not that a constraint
            matched nothing (context/column-filter.jsx:466-470, second in the body
            order). Shared `noResults` key is TaskListOrganism's own zero-matches
            state; this is a distinct meaning. */}
        {activeFilterCount === 0 && (
          <p className="level-filter-popover__empty">
            {t('columnFilters.noConstraint', { column: triggerLabel })}
          </p>
        )}

        {/* ── Inherited tiers (context + view) — collapsible as one unit,
            folded to the single "{N} inherited" button (`uf-column.show-
            what-the-context-and-the-view-alrea`) so authoring a session
            clause doesn't compete with two read-only tiers for the room
            (Phase 4 wires WHEN this folds automatically; here both tier
            heads carry the collapse control and folding either collapses
            both). One bordered line: lock · "{N} inherited" · the clauses
            themselves · chevron (context/column-filter.jsx:474-485). ── */}
        {hasInheritedFilters && inheritedFolded ? (
          <div className="level-filter-popover__inherited">
            <button
              type="button"
              className="level-filter-popover__inherited-summary"
              onClick={() => setInheritedFolded(false)}
              // No `aria-label`: the accessible name is the visible text, and it
              // starts with "{N} inherited" (WCAG 2.5.3 Label in Name — a speech
              // user says what they see); `title` supplies the longer
              // explanation as its description.
              title={t('columnFilters.showInherited')}
              data-datum="uf-column.show-what-the-context-and-the-view-alrea"
              data-kind="button"
            >
              <i className="pi pi-lock level-filter-popover__inherited-lock" aria-hidden="true" />
              <span className="level-filter-popover__inherited-count">
                {t('columnFilters.inheritedCount', { count: inheritedFilterCount })}
              </span>
              {inheritedSummary && (
                <span className="level-filter-popover__inherited-clauses">{inheritedSummary}</span>
              )}
              <i className="pi pi-chevron-down level-filter-popover__inherited-chevron" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <>
        {/* ── Tier 1 · Context — locked (note #3) ───────────────────────── */}
        {renderContextTier()}

        {/* ── Tier 2 · View — per-row ownership (note #5) ───────────────── */}
        {renderViewTier()}
          </>
        )}

        {/* ── Tier 2b · User — personal saved filters ──────────────────────── */}
        {hasAnyFilterLane(byLayer.user) && (
          <div className="level-filter-popover__tier">
            <div className="level-filter-popover__tier-header">
              <span className="level-filter-popover__tier-label">
                <i className="pi pi-user level-filter-popover__tier-icon" aria-hidden="true" />
                {t('columnFilters.provenance.user.heading')}
              </span>
            </div>
            {/* Flat property/lane filters only — groups render as
                `FilterGroupCardMolecule` cards below, matching context/view
                (dev note #1: `FilterChipsDisplayMolecule` expects the
                editor's `SimpleFilterGroup` `.filters` shape, never the
                canonical `.expressions` this tier now authors directly). */}
            {hasAnyFilterLane({ ...byLayer.user, filterGroups: [] }) && (
              <FilterProvenanceGroupMolecule
                layer="user"
                filters={{ ...byLayer.user, filterGroups: [] }}
                locked={isLayerLocked('user', effectiveTargets)}
                showHeading={false}
                getPropertyLabel={getPropertyLabel}
                getPerspectiveLabel={getPerspectiveLabel}
                getRelationshipLabel={getRelationshipLabel}
                getPropertyValueLabel={getPropertyValueLabel}
              />
            )}
            {(byLayer.user.filterGroups ?? []).map((group, i) => (
              <FilterGroupCardMolecule
                key={group.id ?? i}
                group={group}
                provenance="user"
                locked
                lockedReason={t('columnFilters.provenance.user.lockedReason')}
                onView={() => openAdvancedEditorForGroup(group, 'user', false)}
                availableProperties={availableProperties}
                availableRelationships={availableRelationships}
                availablePerspectives={availablePerspectives}
              />
            ))}
          </div>
        )}

        {/* ── Tier 2c · Link — shared filter link clauses (always locked) ────── */}
        {hasAnyFilterLane(byLayer.link) && (
          <div className="level-filter-popover__tier">
            <div className="level-filter-popover__tier-header">
              <span className="level-filter-popover__tier-label">
                <i className="pi pi-link level-filter-popover__tier-icon" aria-hidden="true" />
                {t('columnFilters.provenance.link.heading')}
              </span>
            </div>
            <FilterProvenanceGroupMolecule
              layer="link"
              filters={byLayer.link}
              locked
              showHeading={false}
              getPropertyLabel={getPropertyLabel}
              getPerspectiveLabel={getPerspectiveLabel}
              getRelationshipLabel={getRelationshipLabel}
              getPropertyValueLabel={getPropertyValueLabel}
            />
          </div>
        )}

        {/* ── AND connector (dev note #5) — between locked group tier and session ── */}
        {showAndConnector && (
          <div className="level-filter-popover__and-connector">
            <span className="level-filter-popover__and-label">
              {t('columnFilters.andConnector')}
            </span>
            <span className="level-filter-popover__and-note">
              {t('columnFilters.andConnectorNote')}
            </span>
          </div>
        )}

        {/* ── Tier 3 · Session — always yours (note #6). Always rendered, head
            included: its head is not collapsible. The tree below owns every
            add ("+ Condition" / "+ Add group") — there is no separate mode
            control routing to the Advanced editor. ── */}
        <div className="level-filter-popover__tier">
          <div
            className="level-filter-popover__tier-header"
            data-datum="uf-column.heading#4"
            data-kind="heading"
          >
            <span className="level-filter-popover__tier-label level-filter-popover__tier-label--session">
              <i className="pi pi-clock level-filter-popover__tier-icon" aria-hidden="true" />
              {t('columnFilters.provenance.session.heading')}
              {sessionCount > 0 && (
                <span className="level-filter-popover__tier-count">{sessionCount}</span>
              )}
              {sessionProblemsByLeaf.size > 0 && (
                // The bare number is the visual badge; its meaning ("2
                // problems") is real text for assistive tech, not only a
                // hover `title` (ux-craft: never colour/position alone).
                <span
                  className="level-filter-popover__tier-problem-count"
                  data-testid="level-filter-popover-session-problem-count"
                  title={t('columnFilters.conflict.problemCount', { count: sessionProblemsByLeaf.size })}
                >
                  <span aria-hidden="true">{sessionProblemsByLeaf.size}</span>
                  <span className="level-filter-popover__sr-only">
                    {t('columnFilters.conflict.problemCount', { count: sessionProblemsByLeaf.size })}
                  </span>
                </span>
              )}
            </span>
          </div>
          {/* Read-only session tier — states why, rather than offering edits
              that could never be saved (ux-craft I5). */}
          {sessionLocked && (
            <p className="level-filter-popover__advanced-hint" data-testid="level-filter-popover-session-locked">
              <i className="pi pi-lock" aria-hidden="true" />{' '}
              {sessionLockedReason
                ? t('columnFilters.sessionLocked', { reason: sessionLockedReason })
                : t('columnFilters.provenance.session.lockedReason')}
            </p>
          )}
          {/* The session tier's own clauses — the embedded tree
              (everySurfaceMountsTheSameTree), not a bespoke row list. Held back
              only while the drill-in's draft editor is open on an empty tier,
              so the two never offer the same add side by side. */}
          {(sessionTreeHasRows || !draftEditorOpen) && (
            <>
              <UnifiedFilterTreeMolecule
                value={sessionTreeValue}
                onChange={handleSessionTreeChange}
                readOnly={sessionLocked}
                renderCatalog={renderSessionCatalog}
                problemOf={problemOf}
                availableProperties={sessionAvailableProperties}
                scopeTypeLabels={scopeTypeLabels}
                allowGroups={!focusProperty && !focusPerspective && !focusArc}
                lockPerspective={!!focusPerspective}
                lockArc={!!focusArc}
                hideApplyButton={isColumnPopover}
                arcTemplates={arcTemplates}
                availableRelationships={availableRelationships}
                availablePerspectives={sessionAvailablePerspectives}
                familyTypeSchemas={familyTypeSchemas}
                perspectiveSearchProps={perspectiveSearchProps}
                dense
                readsAs={false}
                datumScope="uf-column"
                container="ui-column"
                className="level-filter-popover__session-tree"
                // An empty tier states there is nothing of the user's own yet —
                // otherwise it reads as "no tier" rather than "nothing added
                // yet" (context/column-filter.jsx:580).
                emptyNote={t('columnFilters.sessionEmpty', { column: triggerLabel })}
                // This popover IS the column/narrow host — TreeMap's
                // column header and (per Phase 5) Atlas's column adapter
                // both mount it as their one filter surface — so the
                // tree's own narrow layout (joiner-above-row) and native
                // drag reordering are this host's job to turn on, not
                // something each consumer opts into separately. Was a real
                // gap: this mount had defaulted to `layout='wide'`/`dnd=
                // false` (the framed uf-simple embed's defaults), so a
                // column's session tier never actually got the narrow
                // shape or draggable rows the spec calls for.
                layout="narrow"
                dnd
              />

              {/* Session-row conflict warning — shown once the conflicting row is
                  complete and valid (dev notes #1, #2). Names the owning tier so
                  the user knows which locked constraint they've contradicted. */}
              {sessionRowConflict && (
                <FilterConflictNoticeMolecule
                  property={getPropertyLabel(sessionRowConflict.property)}
                  lockedLayer={sessionRowConflict.layer}
                  lockedValue={sessionRowConflict.value}
                  contextSystemName={contextSystemName}
                  levelId={scopeId}
                />
              )}
            </>
          )}

          {/* Two-step add-filter flow: property picker → value editor (note #7) */}
          {draftEditorOpen && (
            <div className="level-filter-popover__property-editor">
              {/* Editor header — matches new design "≡ Choose a property…" bar */}
              <div className="level-filter-popover__property-editor-header">
                {pickedProperty ? (
                  <button
                    type="button"
                    className="level-filter-popover__property-editor-back"
                    onClick={() => {
                      setPickedProperty(null);
                      setPickedLeaf(null);
                    }}
                    aria-label={t('common.actions.back', 'Back')}
                  >
                    <i className="pi pi-arrow-left" aria-hidden="true" />
                  </button>
                ) : (
                  <i className="pi pi-list level-filter-popover__property-editor-icon" aria-hidden="true" />
                )}
                <span className="level-filter-popover__property-editor-title">
                  {propertyEditorTitle}
                </span>
                <span className="level-filter-popover__footer-spacer" />
                {!pickedProperty && (
                  <button
                    type="button"
                    className="level-filter-popover__property-editor-collapse"
                    onClick={closeEditor}
                    aria-label={t('columnFilters.collapse', 'Collapse')}
                  >
                    <i className="pi pi-chevron-up" aria-hidden="true" />
                  </button>
                )}
                <button
                  type="button"
                  className="level-filter-popover__property-editor-close"
                  onClick={closeEditor}
                  aria-label={t('columnFilters.close', 'Close')}
                >
                  <i className="pi pi-times" aria-hidden="true" />
                </button>
              </div>

              {/* Step 1: property picker — same UI for both simple and group modes */}
              {!pickedProperty && (
                <AspectCatalogMolecule
                  entries={addFilterCatalogEntries}
                  loading={propertiesLoading}
                  error={propertiesError}
                  onPick={(entry) => {
                    setPickedProperty(entry.key);
                    setPickedLeaf(leafFromCatalogEntry(entry));
                  }}
                  onCancel={closeEditor}
                  emptyScopeHint={scopeNote}
                  scopeTypeLabels={scopeTypeLabels}
                  testIdPrefix="level-filter-property-picker"
                />
              )}

              {/* Step 2: value editor scoped to the picked property */}
              {pickedProperty && (
                <div className="level-filter-popover__editor">
                  {/* The canonical tree (everySurfaceMountsTheSameTree) — the same
                      component the session tier below mounts, seeded with the picked
                      aspect's own leaf. A perspective/relationship/arc pick renders
                      its lane row here, which is what retired this organism's
                      dependence on `UnifiedFilterEditor`'s per-lane sections. */}
                  <UnifiedFilterTreeMolecule
                    value={draftTreeGroup}
                    onChange={handleDraftTreeChange}
                    availableProperties={availableProperties}
                    scopeTypeLabels={scopeTypeLabels}
                    availablePerspectives={availablePerspectives}
                    availableRelationships={availableRelationships}
                    arcTemplates={arcTemplates}
                    familyTypeSchemas={familyTypeSchemas}
                    perspectiveSearchProps={perspectiveSearchProps}
                    lockPerspective={!!focusPerspective}
                    lockArc={!!focusArc}
                    hideApplyButton={isColumnPopover}
                    renderCatalog={({ onPick, onClose }) => (
                      <AspectCatalogMolecule
                        entries={addFilterCatalogEntries}
                        loading={propertiesLoading}
                        error={propertiesError}
                        onPick={(entry) => {
                          onPick(leafFromCatalogEntry(entry));
                          onClose();
                        }}
                        onCancel={onClose}
                        emptyScopeHint={scopeNote}
                        scopeTypeLabels={scopeTypeLabels}
                        testIdPrefix="level-filter-draft-catalog"
                      />
                    )}
                    allowGroups={false}
                    framed={false}
                    readsAs={false}
                    dense
                    datumScope="uf-column"
                    container="ui-column"
                    className="level-filter-popover__draft-tree"
                    // This mount is the same column/narrow host as the session
                    // tree above (:2222) — without it, a lane leaf (perspective/
                    // relationship/arc) falls back to `layout='wide'`'s floating
                    // `OverlayPanelAtom`, a second stacking surface over the
                    // popover the tree already sits in (caught by render-check:
                    // a `focusPerspective` column's fresh-add path opened a
                    // "Perspective filter" dialog on top of the row instead of
                    // expanding in flow).
                    layout="narrow"
                    // Every leaf this mount ever holds is one just picked from
                    // the catalog above (`draftTreeGroup` is seeded fresh per
                    // pick, never a saved leaf) — a lane row's narrow layout has
                    // no `!filter.property`-style "still incomplete" auto-open
                    // the property-row branch gets, so without this the row
                    // lands closed on mount instead of on its editor.
                    defaultOpen
                  />
                </div>
              )}
            </div>
          )}

        </div>

        {conflict && (
          <FilterConflictNoticeMolecule
            property={conflict.property}
            lockedLayer={conflict.layer}
            lockedValue={conflict.value}
            contextSystemName={contextSystemName}
            levelId={scopeId}
          />
        )}

        {/* ── Column visibility (note #8) ────────────────────────────────── */}
        {onHideColumn && <HideColumnButton isColumnHidden={isColumnHidden} onHideColumn={onHideColumn} />}

        {/* ── Footer: Reset · status · Save (notes #9, #10) ─────────────── */}
        {/* Promotion confirmation — shows ~2.6s after save, badge flips SESSION → IN VIEW */}
        {promotedTarget && (
          <div className="level-filter-popover__promotion-confirm">
            <i className="pi pi-check-circle" aria-hidden="true" />
            {t('columnFilters.saveTarget.promotionConfirm', {
              target: t(`columnFilters.saveTarget.${promotedTarget}.label`),
            })}
          </div>
        )}

        {/* what/why/how (ux-craft.md I2): a failed save keeps its row open
            rather than closing over a silent loss. Retryable (5xx/network)
            offers Retry; 403/400 names a different action instead. */}
        {saveError && (
          <MessageAtom
            severity="error"
            className="level-filter-popover__save-error"
            content={
              <div className="level-filter-popover__save-error-body">
                <span className="level-filter-popover__save-error-what">
                  {t('columnFilters.saveError.title')}
                </span>
                <span className="level-filter-popover__save-error-why">
                  {t(
                    saveError.retryable
                      ? 'columnFilters.saveError.retryableDetail'
                      : 'columnFilters.saveError.permissionDetail'
                  )}
                </span>
                {saveError.retryable ? (
                  <ButtonAtom
                    type="button"
                    variant="secondary"
                    size="small"
                    onClick={retrySave}
                    label={t('columnFilters.saveError.retry')}
                  />
                ) : (
                  <span className="level-filter-popover__save-error-how">
                    {t('columnFilters.saveError.permissionAction')}
                  </span>
                )}
              </div>
            }
          />
        )}

        {/* Same what/why/how contract as `saveError` — a failed share leaves
            the user-tier clauses in place (no `onSave` ran) rather than
            silently discarding them. */}
        {shareError && (
          <MessageAtom
            severity="error"
            className="level-filter-popover__share-error"
            content={
              <div className="level-filter-popover__save-error-body">
                <span className="level-filter-popover__save-error-what">
                  {t('columnFilters.shareError.title')}
                </span>
                <span className="level-filter-popover__save-error-why">
                  {t(
                    shareError.retryable
                      ? 'columnFilters.shareError.retryableDetail'
                      : 'columnFilters.shareError.permissionDetail'
                  )}
                </span>
                {shareError.retryable ? (
                  <ButtonAtom
                    type="button"
                    variant="secondary"
                    size="small"
                    onClick={retryShare}
                    label={t('columnFilters.shareError.retry')}
                  />
                ) : (
                  <span className="level-filter-popover__save-error-how">
                    {t('columnFilters.shareError.permissionAction')}
                  </span>
                )}
              </div>
            }
          />
        )}

        {/* Undo for a removed row — not time-boxed, so it stays reachable by
            keyboard until Undo is clicked or another removal replaces it.
            Rendered as part of the footer strip (not a floating message) so it
            stays anchored to the panel edge and reads as one coherent bar. */}
        <div className={`level-filter-popover__footer${removedUndo ? ' level-filter-popover__footer--undo' : ''}`}>
          {removedUndo ? (
            <>
              <i className="pi pi-check level-filter-popover__undo-icon" aria-hidden="true" />
              <span className="level-filter-popover__undo-title">
                {t('columnFilters.removeUndo.title')}
              </span>
              <button
                type="button"
                className="level-filter-popover__undo-action"
                onClick={handleUndoRemove}
              >
                {t('columnFilters.removeUndo.action')}
              </button>
              <span className="level-filter-popover__undo-sep" aria-hidden="true">·</span>
              <span className="level-filter-popover__applied-status level-filter-popover__applied-status--settled">
                {t('columnFilters.changesApplyAsYouGo')}
              </span>
            </>
          ) : (
            <>
          {onResetSession && (
            <button
              type="button"
              className="level-filter-popover__reset"
              onClick={onResetSession}
              title={t('columnFilters.resetMineTitle')}
            >
              {t('columnFilters.resetMine')}
            </button>
          )}
          <span className="level-filter-popover__footer-spacer" />
          {isColumnPopover && linkActive ? (
            <span className="level-filter-popover__applied-status level-filter-popover__applied-status--settled">
              {t('columnFilters.link.active')}
            </span>
          ) : !isColumnPopover && canSave ? (
            <span className="level-filter-popover__applied-status">
              {t('columnFilters.appliedUnsaved')}
            </span>
          ) : (
            <span className="level-filter-popover__applied-status level-filter-popover__applied-status--settled">
              {t('columnFilters.changesApplyAsYouGo')}
            </span>
          )}
          {/* A stalled or not-yet-landed exit needs a repeatable, explicit
              retry (OQ-5: "never exit link mode implicitly") — auto-commit
              has no save click left to double as that retry. */}
          {isColumnPopover && linkActive && onExitSharedLink && (
            <button
              type="button"
              className="level-filter-popover__leave-link"
              onClick={() => onExitSharedLink()}
            >
              {t('columnFilters.link.leave')}
            </button>
          )}
          {!isColumnPopover && (
            <FilterSaveTargetMolecule
              targets={effectiveTargets}
              onSelect={handleSave}
              contextSystemName={contextSystemName}
              levelId={scopeId}
              activeViewName={activeViewDisplayName}
              disabled={!canSave}
              rowDatumId="uf-column.button#25"
            />
          )}
          {/* ── "Share with view…" (OQ-1/OQ-3) — a column's own saved-for-you
              filters, moved into the view on confirm. ─────────────────── */}
          {isColumnPopover && onShareWithView && hasSessionToPromote && (
            <button
              type="button"
              className="level-filter-popover__share-with-view"
              onClick={() => setShareConfirmOpen(true)}
              disabled={viewSaveTarget?.disabled === true}
              title={viewShareLockedReason}
              aria-controls={shareConfirmDialogId}
            >
              {t('columnFilters.shareWithView.action')}
            </button>
          )}
            </>
          )}
        </div>
      </div>
  );

  return (
    <div
      className={`level-filter-popover${trigger ? '' : ' level-filter-popover--panel-only'} ${className}`.trim()}
    >
      {trigger ? (
        <>
          <LevelFilterTrigger
            activeFilterCount={activeFilterCount}
            hasSessionFilter={hasSessionFilter}
            triggerLabel={triggerLabel}
            onToggle={(e) => {
              // Capture the trigger element synchronously before any state updates.
              // PrimeReact reads event.currentTarget asynchronously (deferred to the
              // next frame so the DOM is ready), but React 17+ resets currentTarget
              // to null once the event handler returns — passing it as an explicit
              // target bypasses that and anchors the popover to the correct element.
              const anchor = e.currentTarget as HTMLElement;
              // The draft reset used to live in `onShow` (PrimeReact's `onEntered`,
              // fired ~120ms after this click by a CSSTransition timeout) even
              // though the panel's content mounts and becomes interactive
              // immediately, `unmountOnExit`-gated but not `mountOnEnter`-gated.
              // That left a window, between mount and the transition's timeout,
              // where a fast click-through (or a slower machine delaying the
              // timeout past its usual span) could pick a property/value and then
              // have it silently wiped out when the delayed reset finally ran —
              // reproduced under load as `SessionDraftAppliedUnsaved`'s
              // `p-multiselect-trigger` click opening the panel and then losing it
              // to this exact race. Resetting here instead, at the same
              // synchronous click that starts the open, closes the window: the
              // content mounts already in its fresh state, nothing to race.
              const opening = !overlayRef.current?.isVisible();
              if (opening && (focusProperty || focusPerspective || focusArc)) {
                setShowEditor(!sessionTreeHasRows);
                setPickedProperty(focusProperty ?? focusPerspective ?? focusArc ?? null);
                setPickedLeaf(mintFocusLeaf());
              }
              overlayRef.current?.toggle(e, anchor);
            }}
          />

          <OverlayPanelAtom
            ref={overlayRef}
            className="level-filter-popover__overlay"
            dismissable
            onShow={() => {
              // Broadcast so every other open LevelFilterPopoverOrganism closes.
              window.dispatchEvent(
                new CustomEvent('atlas-filter-open', { detail: instanceId })
              );
              measureRoom();
              onOpen?.();
            }}
            onHide={() => {
              if (!focusProperty && !focusPerspective && !focusArc) closeEditor();
              onClose?.();
            }}
            transitionOptions={panelTransitionOptions}
          >
            {panelBody}
          </OverlayPanelAtom>
        </>
      ) : (
        panelBody
      )}

      {isColumnPopover && onShareWithView && (
        <ConfirmDialogAtom
          id={shareConfirmDialogId}
          visible={shareConfirmOpen}
          message={t('columnFilters.shareWithView.confirmMessage')}
          header={t('columnFilters.shareWithView.confirmTitle')}
          acceptLabel={t('columnFilters.shareWithView.confirm')}
          rejectLabel={t('columnFilters.shareWithView.cancel')}
          accept={handleConfirmShare}
          reject={() => setShareConfirmOpen(false)}
          onHide={() => setShareConfirmOpen(false)}
        />
      )}

      {/* ── Advanced filter editor — full-screen fixed overlay ─────────────────
          Opened from the session tier's Advanced mode or a group card's
          View/Edit. Rendered
          outside the popover so it fills the viewport, not the 420px panel. */}
      {advancedEditorState && (
        <div className="level-filter-popover__advanced-overlay">
          <AdvancedFilterEditorOrganism
            filter={advancedEditorState.initialFilter}
            // A result with no target is a session save.
            onApply={(updatedFilter) => {
              if (isTargetDisabled(AUTHORING_TARGET)) return;
              // The editor's group tree back to canonical groups, added to the
              // session (no session group is ever opened by index, so there is
              // nothing to replace in place).
              applyGroupResult(updatedFilter, AUTHORING_TARGET);
            }}
            onBack={() => {
              setAdvancedEditorState(null);
            }}
            label={`${triggerLabel} · ${t('filterEditor.advanced.columnLabel')}`}
            origin={triggerLabel}
            readOnly={advancedEditorState.readOnly}
            provenance={advancedEditorState.provenance}
            canCopy={advancedEditorState.canCopy && !sessionLocked}
            onCopyToOwn={advancedEditorState.canCopy && !sessionLocked ? () => {
              const groups = advancedEditorState.initialFilter.filterGroups ?? [];
              if (groups.length > 0) handleCopyGroupToSession(simpleGroupToUnified(groups[0]));
              setAdvancedEditorState(null);
            } : undefined}
            saveTargets={effectiveTargets}
            onApplyWithTarget={(updatedFilter, target) => {
              if (isTargetDisabled(target)) return;
              applyGroupResult(updatedFilter, target);
            }}
            contextSystemName={contextSystemName}
            availableProperties={availableProperties}
            orgWideProperties={orgWideProperties}
            scopeTypes={scopeTypes}
            availablePerspectives={availablePerspectives}
            availableRelationships={availableRelationships}
            arcTemplates={arcTemplates}
            familyTypeSchemas={familyTypeSchemas}
            perspectiveSearchProps={perspectiveSearchProps}
          />
        </div>
      )}
    </div>
  );
};

export default LevelFilterPopoverOrganism;
